import React, { useState, useEffect, useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import './Workspace2.css';
import FeedbackButton from './FeedbackButton';
import CategorySelector from './CategorySelector';
import api from './api';
import { useNavigate } from 'react-router-dom';
import { decodeToken, isTokenExpired } from './utils/jwt';
import { trackEvent } from './utils/amplitude';
import { AlertCircle, Download, ExternalLink, Plus, Zap, X, Tag, Edit2, Video, Mic, Square, Check, Trash2, ChevronUp, ChevronDown, RefreshCw, Play, Pause, Loader, CheckCircle, Mail, MessageCircle, Camera, User, Settings } from 'react-feather';
import './App.css';
import './MetaModal.css';
import { useWorkspace } from './WorkspaceContext';
import { useDarkMode } from './DarkModeContext';
import VideoGenerationCard from './VideoGenerationCard';
import axios from 'axios';
import categories from './CategoryData';
import { useVideoGeneration } from './VideoGenerationContext';
import WorkspaceManagement from './WorkspaceManagement';
import { useWorkspaceTab } from './WorkspaceTabContext';
import { Link } from 'react-router-dom';
import SetupStepSparkleBurst from './SetupStepSparkleBurst';

const BOUKAR_USER_ID = '673247877e4ac0a824cbac0d';

// Recording Examples
const RECORDING_EXAMPLES = [
    // Introduction & Context
    "Hi, I noticed your impressive work at [Company] and wanted to reach out.",
    "I've been following [Company]'s growth in the [industry] space.",
    "Your recent post about [topic] really resonated with me.",
    "Your background in [field] caught my attention.",
    "I saw your team is expanding the [department/initiative] and thought I'd reach out.",

    // Value Proposition
    "We help companies like yours improve [metric] by [percentage].",
    "Our solution has helped similar businesses save [X] hours per week.",
    "We've helped other [job title]s increase their team's productivity by [percentage].",
    "Our platform typically reduces [pain point] by [metric].",
    "Companies similar to yours have seen [specific result] within [timeframe].",

    // Industry-Specific
    "The [industry] landscape is rapidly changing, especially regarding [trend].",
    "Many [job title]s we work with struggle with [common challenge].",
    "In today's [industry] environment, [pain point] is more crucial than ever.",
    "With recent changes in [regulation/technology], many companies are looking to [goal].",
    "We've noticed a growing trend in [industry] where [observation].",

    // Problem Recognition
    "Many teams face challenges with [common pain point].",
    "I understand how challenging [specific task] can be.",
    "One common issue we see is [problem description].",
    "Teams often struggle to balance [competing priority A] with [priority B].",
    "The complexity of [process] often leads to [negative outcome].",

    // Solution Overview
    "Our approach simplifies [complex process] through [method].",
    "We've developed a unique solution for [specific challenge].",
    "Our platform automates [manual process] while maintaining [important aspect].",
    "We combine [technology A] with [technology B] to achieve [outcome].",
    "Our solution integrates seamlessly with [common tools] to improve [process].",

    // Results & Benefits
    "Clients typically see ROI within [timeframe].",
    "Our average customer saves [amount] per month.",
    "Teams report [percentage] improvement in [metric] after implementation.",
    "We've helped companies reduce [cost/time] by up to [percentage].",
    "Users experience [benefit] while maintaining [important factor].",

    // Social Proof
    "Companies like [known brand] have achieved [result] using our solution.",
    "We recently helped a [industry] company overcome [challenge].",
    "One of our clients in [industry] reported [specific outcome].",
    "A similar [company type] saw [improvement] within [timeframe].",
    "We've worked with several teams in [industry] to improve [metric].",

    // Use Cases
    "This works particularly well for teams that need to [specific use case].",
    "It's especially useful when you're trying to [achieve goal].",
    "Many teams use this to streamline their [process].",
    "This solution is perfect for organizations looking to [objective].",
    "We've seen great results with teams focusing on [specific goal].",

    // Implementation & Support
    "The implementation process typically takes [timeframe].",
    "Our support team provides [type of support] throughout the process.",
    "We offer comprehensive training for [feature/process].",
    "The onboarding process is designed to minimize [common concern].",
    "Our team handles all aspects of [technical process].",

    // Future-Focused
    "We're constantly developing new features for [emerging need].",
    "Our roadmap includes exciting developments in [area].",
    "We're preparing for [industry trend] with [feature/capability].",
    "Our solution adapts to [changing circumstance] automatically.",
    "We're investing heavily in [future technology/capability]."
];

const Workspace = () => {
    const { darkMode } = useDarkMode();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('raw'); // 'raw' or 'generated'
    const [videos, setVideos] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});
    const [gradientMode, setGradientMode] = useState(() => {
        // Load from localStorage on component mount
        const saved = localStorage.getItem('gradientMode');
        console.log('🔍 Loading gradientMode from localStorage:', saved);
        const result = saved ? JSON.parse(saved) : false;
        console.log('🔍 Parsed gradientMode:', result);
        return result;
    });

    const [selectedGradient, setSelectedGradient] = useState(() => {
        // Load from localStorage on component mount
        const saved = localStorage.getItem('selectedGradient');
        return saved || 'classic';
    });

    const [showGradientPalette, setShowGradientPalette] = useState(false);

    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Gradient presets
    const gradientPresets = {
        classic: 'linear-gradient(150deg, #ff1e78 0%, #ff7800 30%,  #a03cff 80%, #ff2896 100%)',
        candy: 'linear-gradient(135deg, #D34C5B 0%, #AFE2B4 50%, #89F2CF 75%, #74E5E3 90%, #5A9ADB 100%)',
        bubble: 'linear-gradient(135deg, #73D9F6 0%, #BFAEEF 35%, #B4ECF3 42%, #B9FADA 50%, #E0ABC7 75%, #B1F1FA 90%, #C774BB 100%)',
        aurora: 'linear-gradient(135deg, #bedbfa 0%, #e6c8ff 25%, #ffaf9b 50%, #caecbd 75%, #b1f1fa 100%)',
        dark: 'linear-gradient(135deg, #2D333C 0%, #21252E 35%, #2D333C 42%, #2B303A 50%, #1B1F27 75%, #2A2D36 90%, #131620 100%)',
        sunset: 'linear-gradient(180deg, #E8D5FF 0%, #FFC1E3 20%, #FFD4A3 40%, #87CEEB 60%, #4A90E2 80%, #1E3A8A 100%)',
        horizon: 'linear-gradient(90deg, #FFD4A3 0%, #B794F6 50%, #E0F2FE 100%)',
        pastel: 'linear-gradient(180deg, #B794F6 0%, #FFC1E3 50%, #E0F2FE 100%)'
    };

    // Get current gradient
    const getCurrentGradient = () => {
        return gradientPresets[selectedGradient] || gradientPresets.classic;
    };

    // Save gradient mode to localStorage whenever it changes
    useEffect(() => {
        console.log('💾 Saving gradientMode to localStorage:', gradientMode);
        localStorage.setItem('gradientMode', JSON.stringify(gradientMode));
    }, [gradientMode]);

    // Save selected gradient to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('selectedGradient', selectedGradient);
    }, [selectedGradient]);

    // Close gradient palette when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showGradientPalette && !event.target.closest('[data-gradient-palette]')) {
                setShowGradientPalette(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showGradientPalette]);

    const [isDragging, setIsDragging] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [newTag, setNewTag] = useState('');
    const [newDescription, setNewDescription] = useState('');

    // User info
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState(null);

    // Profile info
    const [profileComplete, setProfileComplete] = useState(false);
    const [userProfile, setUserProfile] = useState({
        currentSituation: '',
        shortTermGoals: '',
        longTermGoals: '',
        websiteUrl: '',
        prospectingProfile: {
            currentRole: '',
            companyPurpose: '',
            targetAudience: '',
            howToBePerceived: '',
            currentMarketingStrategy: '',
            contentIdeas: '',
            instagramUrl: '',
            inspiringBrands: '',
            competitors: '',
            targetChallenge: ''
        },
        isSubscribed: false,
        subscriptionPlan: 'none'
    });

    const [userInfo, setUserInfo] = useState({
        picture: '',
        name: ''
    });

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [onboardingCompleted, setOnboardingCompleted] = useState(true); // true until we know otherwise (avoids flash)
    const [referralSource, setReferralSource] = useState('');
    // Workspace setup progress (3 steps: raw videos, cuts, studio video)
    const [setupStep1, setSetupStep1] = useState(false);
    const [setupStep2, setSetupStep2] = useState(false);
    const [setupStep3, setSetupStep3] = useState(false);
    const [setupStep4, setSetupStep4] = useState(false);
    const [setupStep5, setSetupStep5] = useState(false);
    const [showSetupConfetti, setShowSetupConfetti] = useState(false);
    const setupConfettiCanvasRef = useRef(null);
    const setupStepsRef = useRef(null);
    const setupOnboardingCompleteCalledRef = useRef(false);
    const prevSetupStepsRef = useRef(null);
    const setupStepsWorkspaceIdRef = useRef(null);
    const [setupStepSparkles, setSetupStepSparkles] = useState({});
    const [setupStepsInView, setSetupStepsInView] = useState(true);
    const [profilePictureUploading, setProfilePictureUploading] = useState(false);
    const [profilePictureError, setProfilePictureError] = useState('');
    const profilePictureInputRef = useRef(null);
    const [hasVisited, setHasVisited] = useState(true);

    // Navigation (for redirecting to landing page)
    const [hasCreatedFirstVideo, setHasCreatedFirstVideo] = useState(false);

    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState('');
    const [isBetaUser, setIsBetaUser] = useState(false);
    const [backendError, setBackendError] = useState('');

    // Heroku wake-up handling
    const SERVER_STARTING_MESSAGE_DELAY_MS = 3000;
    const [serverStarting, setServerStarting] = useState(false);
    const [showServerStartingMessage, setShowServerStartingMessage] = useState(false);
    const [countdown, setCountdown] = useState(20);
    const [serverWakeProgress, setServerWakeProgress] = useState(5);
    const wakingPollRef = useRef(null);
    const initializeUserRef = useRef(null);
    const progressTimersRef = useRef([]);
    const serverStartingMessageTimerRef = useRef(null);

    // Delay showing the wake-up banner so fast recoveries don't flash it
    useEffect(() => {
        if (serverStarting) {
            serverStartingMessageTimerRef.current = setTimeout(() => {
                setShowServerStartingMessage(true);
            }, SERVER_STARTING_MESSAGE_DELAY_MS);
        } else {
            clearTimeout(serverStartingMessageTimerRef.current);
            serverStartingMessageTimerRef.current = null;
            setShowServerStartingMessage(false);
        }

        return () => {
            clearTimeout(serverStartingMessageTimerRef.current);
        };
    }, [serverStarting]);

    // Manage fake progress bar for server wake-up
    useEffect(() => {
        if (showServerStartingMessage) {
            // Reset to 5% when starting
            setServerWakeProgress(0);

            // Clear any existing timers
            progressTimersRef.current.forEach(timer => clearTimeout(timer));
            progressTimersRef.current = [];

            // Set up progress updates at specific timestamps
            const timers = [
                setTimeout(() => setServerWakeProgress(5), 1500),  // 1.5s: 5%
                setTimeout(() => setServerWakeProgress(15), 3000),  // 3s: 15%
                setTimeout(() => setServerWakeProgress(20), 5000),  // 5s: 20%
                setTimeout(() => setServerWakeProgress(45), 9000),  // 9s: 45%
                setTimeout(() => setServerWakeProgress(55), 11000), // 11s: 55%
                setTimeout(() => setServerWakeProgress(75), 16000), // 16s: 75%
                setTimeout(() => setServerWakeProgress(90), 19000), // 19s: 90%
                setTimeout(() => setServerWakeProgress(95), 22000), // 22s: 95%
            ];

            progressTimersRef.current = timers;
        } else {
            // Clear timers when serverStarting becomes false
            progressTimersRef.current.forEach(timer => clearTimeout(timer));
            progressTimersRef.current = [];
            setServerWakeProgress(0); // Reset progress
        }

        return () => {
            // Cleanup on unmount
            progressTimersRef.current.forEach(timer => clearTimeout(timer));
        };
    }, [showServerStartingMessage]);

    // Countdown timer for server wake-up
    useEffect(() => {
        if (!showServerStartingMessage) {
            setCountdown(20);
            return;
        }

        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [showServerStartingMessage]);

    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info'); // Can be 'info', 'error', or 'success'

    // Modal states
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Voice cloning state variables - moved to ProfileModal component

    const {
        videoPrompt, setVideoPrompt,
        selectedCategories, setSelectedCategories,
        generatedVideoUrl, setGeneratedVideoUrl,
        currentWorkspace,
        generateInviteLink,
        joinWorkspace,
        updateMemberRole,
        removeMember,
        leaveWorkspace,
        hasPermission,
        getUserRole,
        updateWorkspace
    } = useWorkspace();

    const { setActiveTab: setWorkspaceTab } = useWorkspaceTab() || {};

    const [showMetaModal, setShowMetaModal] = useState(false);

    const [availableInterests, setAvailableInterests] = useState([]);
    const {
        selectedInterests,
        videoCards,
        analyzing,
        sourceVideo,
        selectedModel,
        credits,
        handleInterestSelect,
        updateCardPrompt,
        generateVideo,
        generateAllVideos,
        downloadVideo,
        setVideo,
        setModel,
        clearErrors,
    } = useVideoGeneration();

    const [generatedVideos, setGeneratedVideos] = useState([]);

    useEffect(() => {
        localStorage.setItem('preferredVideoModel', selectedModel);
    }, [selectedModel]);

    // Restore persisted setup steps from workspace (survives page refresh)
    useEffect(() => {
        setupOnboardingCompleteCalledRef.current = false;
        const progress = currentWorkspace?.setupStepsProgress || {};
        setSetupStep2(!!progress.visitedCutsTab);
        setSetupStep3(!!progress.visitedProfileTab);
        setSetupStep4(!!progress.visitedVoiceTab);
        setSetupStep5(!!progress.studioVideoCreated);
    }, [currentWorkspace?._id, currentWorkspace?.setupStepsProgress]);

    const setupRequiredComplete = setupStep1 && setupStep2 && setupStep3 && setupStep5;

    // Mark onboarding complete once all required setup steps are done
    useEffect(() => {
        if (!setupRequiredComplete || onboardingCompleted || setupOnboardingCompleteCalledRef.current) return;
        // Legacy: setup checklist used to call completeOnboarding — now OnboardingOverlay does.
        return;
        setupOnboardingCompleteCalledRef.current = true;
        api.completeOnboarding()
            .then(() => {
                setOnboardingCompleted(true);
                trackEvent('onboarding_completed', {
                    workspaceId: currentWorkspace?._id,
                    viaSetupSteps: true
                });
            })
            .catch((err) => {
                setupOnboardingCompleteCalledRef.current = false;
                console.error('Error completing onboarding after setup steps:', err);
            });
    }, [setupRequiredComplete, onboardingCompleted, currentWorkspace?._id]);

    const setupSteps = [
        { done: setupStep1, label: 'Add content to your Raw Videos library', tab: 'raw' },
        { done: setupStep2, label: 'Pick the AI-selected cuts you want to keep', tab: 'cuts' },
        { done: setupStep3, label: 'Add a bio to your profile', tab: 'profile' },
        { done: setupStep4, label: 'Clone your voice in the Voice tab (optional)', tab: 'voice', optional: true },
        { done: setupStep5, label: 'Create a video in the studio', tab: 'studio' }
    ];
    const currentSetupStepIndex = setupSteps.findIndex((s) => !s.done && !s.optional);

    useEffect(() => {
        const current = [setupStep1, setupStep2, setupStep3, setupStep4, setupStep5];
        const workspaceId = currentWorkspace?._id ?? null;

        if (setupStepsWorkspaceIdRef.current !== workspaceId) {
            setupStepsWorkspaceIdRef.current = workspaceId;
            prevSetupStepsRef.current = current;
            setSetupStepSparkles({});
            return;
        }

        if (prevSetupStepsRef.current === null) {
            prevSetupStepsRef.current = current;
            return;
        }

        const completedIndexes = [];
        current.forEach((done, index) => {
            if (done && !prevSetupStepsRef.current[index]) {
                completedIndexes.push(index);
            }
        });

        if (completedIndexes.length > 0) {
            setSetupStepSparkles((prev) => {
                const next = { ...prev };
                completedIndexes.forEach((index) => {
                    next[index] = Date.now();
                });
                return next;
            });
        }

        prevSetupStepsRef.current = current;
    }, [setupStep1, setupStep2, setupStep3, setupStep4, setupStep5, currentWorkspace?._id]);

    const clearSetupStepSparkle = (index) => {
        setSetupStepSparkles((prev) => {
            if (!prev[index]) return prev;
            const next = { ...prev };
            delete next[index];
            return next;
        });
    };

    const navigateToSetupTab = (tab) => {
        if (!tab || typeof setWorkspaceTab !== 'function') return;
        setWorkspaceTab(tab);
        const tabContent = document.querySelector('.workspace-management-content');
        if (tabContent) tabContent.scrollTop = 0;
    };

    // Show floating "current step" pill when user scrolls past the inline setup steps bar
    useEffect(() => {
        const el = setupStepsRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => setSetupStepsInView(entry.isIntersecting),
            { threshold: 0.1, rootMargin: '0px 0px -20% 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [currentWorkspace?._id]);

    // Confetti when workspace setup is completed (required steps done)
    useEffect(() => {
        if (!showSetupConfetti || !setupConfettiCanvasRef.current) return;
        const canvas = setupConfettiCanvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        const confetti = [];
        const colors = ['#e1f679', '#0066cc', '#28a745', '#ffc107', '#dc3545', '#8b5cf6'];
        for (let i = 0; i < 150; i++) {
            confetti.push({
                x: Math.random() * canvas.width,
                y: -10,
                width: Math.random() * 10 + 5,
                height: Math.random() * 10 + 5,
                speed: Math.random() * 2 + 5,
                rotation: Math.random() * 360,
                rotationSpeed: Math.random() * 10 - 20,
                color: colors[Math.floor(Math.random() * colors.length)],
                opacity: Math.random() * 0.5 + 0.5
            });
        }
        let animationId;
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            confetti.forEach((particle, index) => {
                ctx.save();
                ctx.globalAlpha = particle.opacity;
                ctx.fillStyle = particle.color;
                ctx.translate(particle.x, particle.y);
                ctx.rotate((particle.rotation * Math.PI) / 180);
                ctx.fillRect(-particle.width / 2, -particle.height / 2, particle.width, particle.height);
                ctx.restore();
                particle.y += particle.speed;
                particle.x += Math.sin(particle.y * 0.01) * 4;
                particle.rotation += particle.rotationSpeed;
                if (particle.y > canvas.height + 20) confetti.splice(index, 1);
            });
            if (confetti.length > 0) animationId = requestAnimationFrame(animate);
            else setShowSetupConfetti(false);
        };
        animate();
        return () => { if (animationId) cancelAnimationFrame(animationId); };
    }, [showSetupConfetti]);

    // Fetch user info on component mount
    useEffect(() => {
        const initializeUser = async () => {
            const params = new URLSearchParams(window.location.search);
            const tokenFromUrl = params.get('token');
            let token = tokenFromUrl || localStorage.getItem('token');

            console.log("params:", params);
            console.log("tokenFromUrl:", tokenFromUrl);
            console.log('Token from localStorage:', localStorage.getItem('token').substring(0, 10));
            console.log("token:", token.substring(0, 10));

            if (tokenFromUrl) {
                // Save the token to local storage
                localStorage.setItem('token', tokenFromUrl);
                console.log('Token saved to localStorage:', tokenFromUrl);

                // Clean up the URL to remove the token
                window.history.replaceState({}, document.title, window.location.pathname);
            }

            // Validate the token
            if (!token || isTokenExpired(token)) {
                console.log('Token is invalid or expired. Redirecting to landing page.');
                navigate('/');
                return;
            }

            // Decode the token to get the user ID and fetch files
            const decoded = decodeToken(token);

            // If returning from Stripe Checkout, verify subscription and refresh profile
            const urlParams = new URLSearchParams(window.location.search);
            const checkoutSessionId = urlParams.get('session_id');
            if (checkoutSessionId) {
                try {
                    const statusResp = await api.getSubscriptionStatus(checkoutSessionId);
                    if (statusResp.data?.isActive) {
                        // Remove session_id from URL
                        window.history.replaceState({}, document.title, window.location.pathname);
                        // Refresh profile to reflect subscription status
                        await api.confirmSubscription(checkoutSessionId);
                        const profileResponse = await api.getUserProfile();
                        if (profileResponse.data) {
                            setUserProfile(prev => ({
                                ...prev,
                                isSubscribed: profileResponse.data.isSubscribed,
                                subscriptionPlan: profileResponse.data.subscriptionPlan || 'none',
                                subscriptionStatus: profileResponse.data.subscriptionStatus || 'active'
                            }));
                        }
                    }
                } catch (e) {
                    console.warn('Failed to verify subscription status after checkout:', e?.message);
                }
            }

            // Check for pending invite token after successful authentication
            const pendingInviteToken = localStorage.getItem('pendingInviteToken');
            if (pendingInviteToken) {
                console.log('Found pending invite token, attempting to join workspace:', pendingInviteToken);
                try {
                    const workspace = await joinWorkspace(pendingInviteToken, 'editor');
                    console.log('Successfully joined workspace via pending invite:', workspace);

                    // Show success message
                    setMessage(`Successfully joined workspace: ${workspace.name}`);
                    setMessageType('success');

                    // Clear the pending invite token
                    localStorage.removeItem('pendingInviteToken');
                } catch (error) {
                    console.error('Failed to join workspace via pending invite:', error);
                    setMessage('Failed to join workspace. The invite may be invalid or expired.');
                    setMessageType('error');
                    // Clear the pending invite token even if it failed
                    localStorage.removeItem('pendingInviteToken');
                }
            }

            console.log('decoded Token:', decoded);
            console.log('decoded username:', decoded.name);
            console.log('decoded ID:', decoded.userId);

            if (decoded) {
                setUserId(decoded.userId); // Set the user ID
                setUserName(decoded.name); // Extract the name from the token
                setUserInfo(prev => ({ ...prev, name: decoded.name }));

                try {
                    setBackendError(''); // Clear any previous error

                    // Fetch profile
                    const profileResponse = await api.getUserProfile();
                    console.log('Profile response:', profileResponse.data);

                    if (profileResponse.data) {
                        setOnboardingCompleted(!!profileResponse.data.onboardingCompleted);
                        setReferralSource(profileResponse.data.onboardingAnswers?.referralSource || '');
                        setUserProfile({
                            ...profileResponse.data.profile,
                            websiteUrl: profileResponse.data.profile?.websiteUrl || '',
                            // Include subtitle colors from profile
                            subtitleFontColor: profileResponse.data.profile?.subtitleFontColor || '#ffffff',
                            subtitleOutlineColor: profileResponse.data.profile?.subtitleOutlineColor || '#000000',
                            subtitleBackgroundColor: profileResponse.data.profile?.subtitleBackgroundColor || 'transparent',
                            subtitleOutlineEnabled: profileResponse.data.profile?.subtitleOutlineEnabled !== undefined ? profileResponse.data.profile.subtitleOutlineEnabled : false,
                            prospectingProfile: profileResponse.data.prospectingProfile || {
                                currentRole: '',
                                companyPurpose: '',
                                targetAudience: ''
                            },
                            socialMedia: profileResponse.data.socialMedia || {
                                mainPlatform: 'instagram',
                                instagram: { handle: '', currentFollowers: 0, targetFollowers: 0 },
                                tiktok: { handle: '', currentFollowers: 0, targetFollowers: 0 },
                                youtube: { handle: '', currentFollowers: 0, targetFollowers: 0 }
                            },
                            videoProduction: profileResponse.data.videoProduction || {
                                currentVideosPerMonth: 0,
                                targetVideosPerMonth: 0
                            },
                            hasCreatedFirstVideo: profileResponse.data.hasCreatedFirstVideo,
                            isBetaUser: profileResponse.data.isBetaUser,
                            isSubscribed: profileResponse.data.isSubscribed,
                            subscriptionPlan: profileResponse.data.subscriptionPlan || 'none',
                            stripeCustomerId: profileResponse.data.stripeCustomerId,
                            subscriptionStatus: profileResponse.data.subscriptionStatus
                        });

                        setProfileComplete(profileResponse.data.profileComplete);
                        setHasVisited(profileResponse.data.hasVisited);
                        setUserInfo(prev => ({
                            ...prev,
                            picture: profileResponse.data.picture,
                            name: profileResponse.data.name || decoded.name
                        }));

                        // if (!profileResponse.data.hasVisited) {
                        //     setShowProfileModal(true);
                        //     await api.markVisited();
                        // }

                        // Track workspace loaded (funnel completion)
                        trackEvent('workspace_loaded', {
                            userId: decoded.userId,
                            isNewUser: !profileResponse.data.onboardingCompleted
                        });
                    }

                } catch (error) {
                    console.error('Error initializing:', error);
                    // If no response (network/timeout) or 5xx, assume dyno cold start
                    const isLikelyColdStart = !error.response || (error.response.status >= 500);
                    if (isLikelyColdStart) {
                        setServerStarting(true);
                        setBackendError('');
                        if (!wakingPollRef.current) {
                            wakingPollRef.current = setInterval(async () => {
                                try {
                                    const res = await api.health();
                                    if (res.status === 200 && res.data?.status === 'ok') {
                                        clearInterval(wakingPollRef.current);
                                        wakingPollRef.current = null;
                                        setServerStarting(false);
                                        setBackendError('');
                                        if (initializeUserRef.current) {
                                            initializeUserRef.current();
                                        }
                                    }
                                } catch (e) {
                                    // keep polling until server is up
                                }
                            }, 3000);
                        }
                    } else {
                        setBackendError('Unable to load data. Please try again later.');
                        if (error.response?.status === 401) {
                            navigate('/');
                        }
                    }
                }
            } else {
                setBackendError('Hey people, Boukar here, I was coding and did some shit, server down, will fix asap, sorry for that, thanks for using the app (help!)');//setBackendError('Unable to load data, server down, please try again later');
            }
        };

        initializeUserRef.current = initializeUser;
        initializeUser();

        return () => {
            if (wakingPollRef.current) {
                clearInterval(wakingPollRef.current);
                wakingPollRef.current = null;
            }
        };
    }, [navigate]);

    // Independent health check on mount to handle cases before token decode
    useEffect(() => {
        let cancelled = false;
        let showTimer = setTimeout(() => {
            if (!cancelled) {
                setServerStarting(true);
                setBackendError('');
            }
        }, 1000);

        const startPolling = () => {
            wakingPollRef.current = setInterval(async () => {
                try {
                    const h = await api.health();
                    if (h.status === 200 && h.data?.status === 'ok') {
                        clearInterval(wakingPollRef.current);
                        wakingPollRef.current = null;
                        if (!cancelled) {
                            setServerStarting(false);
                            setBackendError('');
                            if (initializeUserRef.current) {
                                initializeUserRef.current();
                            }
                        }
                    }
                } catch (_) {
                    // keep polling
                }
            }, 3000);
        };

        const checkHealth = async () => {
            try {
                const res = await api.health();
                if (!(res.status === 200 && res.data?.status === 'ok')) {
                    throw new Error('server not ready');
                }
                if (!cancelled) {
                    clearTimeout(showTimer);
                    setServerStarting(false);
                }
            } catch (e) {
                if (cancelled) return;
                clearTimeout(showTimer);
                setServerStarting(true);
                if (!wakingPollRef.current) {
                    startPolling();
                }
            }
        };

        checkHealth();
        return () => {
            cancelled = true;
            clearTimeout(showTimer);
        };
    }, []);

    // Profile Modal Component
    const ProfileModal = () => {
        // Voice cloning state variables - moved inside ProfileModal
        const [isRecording, setIsRecording] = useState(false);
        const [recordings, setRecordings] = useState([]);
        const [mediaRecorder, setMediaRecorder] = useState(null);
        const [mediaStream, setMediaStream] = useState(null);
        const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
        const [isTestingVoice, setIsTestingVoice] = useState(false);
        const [audioUrl, setAudioUrl] = useState('');
        const [isPlayingVoice, setIsPlayingVoice] = useState(false);
        const audioRef = useRef(null);
        const [recordingDuration, setRecordingDuration] = useState(0);
        const [timerInterval, setTimerInterval] = useState(null);
        const [isSavingRecording, setIsSavingRecording] = useState(false);
        const [recordingLength, setRecordingLength] = useState({});
        const [showRecordingSection, setShowRecordingSection] = useState(false);
        const [showExamples, setShowExamples] = useState(false);
        const [currentExample, setCurrentExample] = useState(0);
        const [displayedExamples, setDisplayedExamples] = useState([]);
        const [isSaving, setIsSaving] = useState(false);
        const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'
        const [userVoiceId, setUserVoiceId] = useState(null);
        const [testAudioUrl, setTestAudioUrl] = useState('');
        const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
        const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
        const [hasSavedToBackend, setHasSavedToBackend] = useState(false);
        const [savedFormData, setSavedFormData] = useState(null);
        // Subtitle color settings
        const [subtitleFontColor, setSubtitleFontColor] = useState('#ffffff');
        const [subtitleOutlineColor, setSubtitleOutlineColor] = useState('#000000');
        const [subtitleBackgroundColor, setSubtitleBackgroundColor] = useState('transparent');
        const [subtitleOutlineEnabled, setSubtitleOutlineEnabled] = useState(false);

        // Color picker states
        const [showFontColorPicker, setShowFontColorPicker] = useState(false);
        const [showOutlineColorPicker, setShowOutlineColorPicker] = useState(false);

        // Close color pickers when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (showFontColorPicker || showOutlineColorPicker) {
                    // Check if click is outside color picker elements
                    const fontPickerElement = document.querySelector('[data-font-color-picker]');
                    const outlinePickerElement = document.querySelector('[data-outline-color-picker]');

                    if (fontPickerElement && !fontPickerElement.contains(event.target)) {
                        setShowFontColorPicker(false);
                    }
                    if (outlinePickerElement && !outlinePickerElement.contains(event.target)) {
                        setShowOutlineColorPicker(false);
                    }
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [showFontColorPicker, showOutlineColorPicker]);

        // État local pour le formulaire
        const [formData, setFormData] = useState({
            currentSituation: userProfile.currentSituation || '',
            shortTermGoals: userProfile.shortTermGoals || '',
            longTermGoals: userProfile.longTermGoals || '',
            websiteUrl: userProfile.websiteUrl || '',
            prospectingProfile: {
                currentRole: userProfile.prospectingProfile?.currentRole || '',
                companyPurpose: userProfile.prospectingProfile?.companyPurpose || '',
                targetAudience: userProfile.prospectingProfile?.targetAudience || '',
                howToBePerceived: userProfile.prospectingProfile?.howToBePerceived || '',
                currentMarketingStrategy: userProfile.prospectingProfile?.currentMarketingStrategy || '',
                contentIdeas: userProfile.prospectingProfile?.contentIdeas || '',
                instagramUrl: userProfile.prospectingProfile?.instagramUrl || '',
                inspiringBrands: userProfile.prospectingProfile?.inspiringBrands || '',
                competitors: userProfile.prospectingProfile?.competitors || '',
                targetChallenge: userProfile.prospectingProfile?.targetChallenge || ''
            },
            socialMedia: {
                mainPlatform: userProfile.socialMedia?.mainPlatform || 'instagram',
                instagram: {
                    handle: userProfile.socialMedia?.instagram?.handle || '',
                    currentFollowers: userProfile.socialMedia?.instagram?.currentFollowers || 0,
                    targetFollowers: userProfile.socialMedia?.instagram?.targetFollowers || 0
                },
                tiktok: {
                    handle: userProfile.socialMedia?.tiktok?.handle || '',
                    currentFollowers: userProfile.socialMedia?.tiktok?.currentFollowers || 0,
                    targetFollowers: userProfile.socialMedia?.tiktok?.targetFollowers || 0
                },
                youtube: {
                    handle: userProfile.socialMedia?.youtube?.handle || '',
                    currentFollowers: userProfile.socialMedia?.youtube?.currentFollowers || 0,
                    targetFollowers: userProfile.socialMedia?.youtube?.targetFollowers || 0
                }
            },
            videoProduction: {
                currentVideosPerMonth: userProfile.videoProduction?.currentVideosPerMonth || 0,
                targetVideosPerMonth: userProfile.videoProduction?.targetVideosPerMonth || 0
            }
        });

        // Initialize formData and savedFormData when modal opens or userProfile changes
        useEffect(() => {
            const initialData = {
                currentSituation: userProfile.currentSituation || '',
                shortTermGoals: userProfile.shortTermGoals || '',
                longTermGoals: userProfile.longTermGoals || '',
                websiteUrl: userProfile.websiteUrl || '',
                prospectingProfile: {
                    currentRole: userProfile.prospectingProfile?.currentRole || '',
                    companyPurpose: userProfile.prospectingProfile?.companyPurpose || '',
                    targetAudience: userProfile.prospectingProfile?.targetAudience || '',
                    howToBePerceived: userProfile.prospectingProfile?.howToBePerceived || '',
                    currentMarketingStrategy: userProfile.prospectingProfile?.currentMarketingStrategy || '',
                    contentIdeas: userProfile.prospectingProfile?.contentIdeas || '',
                    instagramUrl: userProfile.prospectingProfile?.instagramUrl || '',
                    inspiringBrands: userProfile.prospectingProfile?.inspiringBrands || '',
                    competitors: userProfile.prospectingProfile?.competitors || '',
                    targetChallenge: userProfile.prospectingProfile?.targetChallenge || ''
                },
                socialMedia: {
                    mainPlatform: userProfile.socialMedia?.mainPlatform || 'instagram',
                    instagram: {
                        handle: userProfile.socialMedia?.instagram?.handle || '',
                        currentFollowers: userProfile.socialMedia?.instagram?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.instagram?.targetFollowers || 0
                    },
                    tiktok: {
                        handle: userProfile.socialMedia?.tiktok?.handle || '',
                        currentFollowers: userProfile.socialMedia?.tiktok?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.tiktok?.targetFollowers || 0
                    },
                    youtube: {
                        handle: userProfile.socialMedia?.youtube?.handle || '',
                        currentFollowers: userProfile.socialMedia?.youtube?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.youtube?.targetFollowers || 0
                    }
                },
                videoProduction: {
                    currentVideosPerMonth: userProfile.videoProduction?.currentVideosPerMonth || 0,
                    targetVideosPerMonth: userProfile.videoProduction?.targetVideosPerMonth || 0
                }
            };

            setFormData(initialData);
            // Use the subtitle colors from userProfile state (which now includes them)
            setSubtitleFontColor(userProfile.subtitleFontColor || '#ffffff');
            setSubtitleOutlineColor(userProfile.subtitleOutlineColor || '#000000');
            setSubtitleBackgroundColor(userProfile.subtitleBackgroundColor || 'transparent');
            setSubtitleOutlineEnabled(userProfile.subtitleOutlineEnabled !== undefined ? userProfile.subtitleOutlineEnabled : false);
            setSavedFormData({
                ...initialData,
                subtitleFontColor: userProfile.subtitleFontColor || '#ffffff',
                subtitleOutlineColor: userProfile.subtitleOutlineColor || '#000000',
                subtitleBackgroundColor: userProfile.subtitleBackgroundColor || 'transparent',
                subtitleOutlineEnabled: userProfile.subtitleOutlineEnabled !== undefined ? userProfile.subtitleOutlineEnabled : false
            }); // Set initial baseline
        }, [userProfile]); // Depend on userProfile so it updates when data is loaded

        // Save to backend only (no parent state update)
        const saveToBackend = async () => {
            try {
                setIsSaving(true);
                setSaveStatus('saving');

                trackEvent('profile_update_started', {
                    userId,
                    fieldsUpdated: [
                        ...Object.keys(formData).filter(key =>
                            key !== 'prospectingProfile' &&
                            key !== 'socialMedia' &&
                            formData[key] &&
                            typeof formData[key] === 'string' &&
                            formData[key].trim() !== ''
                        ),
                        ...Object.keys(formData.prospectingProfile).filter(key =>
                            formData.prospectingProfile[key] &&
                            typeof formData.prospectingProfile[key] === 'string' &&
                            formData.prospectingProfile[key].trim() !== ''
                        )
                    ]
                });

                // Structure the data correctly for the API
                const profileData = {
                    profile: {
                        currentSituation: formData.currentSituation,
                        shortTermGoals: formData.shortTermGoals,
                        longTermGoals: formData.longTermGoals,
                        websiteUrl: formData.websiteUrl,
                        subtitleFontColor: subtitleFontColor,
                        subtitleOutlineColor: subtitleOutlineColor,
                        subtitleBackgroundColor: subtitleBackgroundColor,
                        subtitleOutlineEnabled: subtitleOutlineEnabled
                    }
                };

                // Update main profile
                const profileResponse = await api.updateProfile(profileData);

                // Update prospecting profile separately
                const prospectingData = {
                    currentRole: formData.prospectingProfile.currentRole,
                    companyPurpose: formData.prospectingProfile.companyPurpose,
                    targetAudience: formData.prospectingProfile.targetAudience,
                    howToBePerceived: formData.prospectingProfile.howToBePerceived,
                    currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                    contentIdeas: formData.prospectingProfile.contentIdeas,
                    instagramUrl: formData.prospectingProfile.instagramUrl,
                    inspiringBrands: formData.prospectingProfile.inspiringBrands,
                    competitors: formData.prospectingProfile.competitors,
                    targetChallenge: formData.prospectingProfile.targetChallenge,
                    socialMedia: formData.socialMedia,
                    videoProduction: formData.videoProduction
                };

                await api.updateProspectingProfile(prospectingData);

                trackEvent('profile_update_successful', {
                    userId,
                    profileComplete: profileResponse.data.profileComplete
                });

                // Clear unsaved changes flag and mark as saved to backend
                setHasUnsavedChanges(false);
                setHasSavedToBackend(true);

                // Update the saved baseline to current form data
                setSavedFormData({
                    currentSituation: formData.currentSituation,
                    shortTermGoals: formData.shortTermGoals,
                    longTermGoals: formData.longTermGoals,
                    websiteUrl: formData.websiteUrl,
                    subtitleFontColor: subtitleFontColor,
                    subtitleOutlineColor: subtitleOutlineColor,
                    subtitleBackgroundColor: subtitleBackgroundColor,
                    subtitleOutlineEnabled: subtitleOutlineEnabled,
                    prospectingProfile: {
                        currentRole: formData.prospectingProfile.currentRole,
                        companyPurpose: formData.prospectingProfile.companyPurpose,
                        targetAudience: formData.prospectingProfile.targetAudience,
                        howToBePerceived: formData.prospectingProfile.howToBePerceived,
                        currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                        contentIdeas: formData.prospectingProfile.contentIdeas,
                        instagramUrl: formData.prospectingProfile.instagramUrl,
                        inspiringBrands: formData.prospectingProfile.inspiringBrands,
                        competitors: formData.prospectingProfile.competitors,
                        targetChallenge: formData.prospectingProfile.targetChallenge
                    },
                    socialMedia: formData.socialMedia,
                    videoProduction: formData.videoProduction
                });

                // Show success status
                setSaveStatus('saved');
                setIsSaving(false);

                // Reset status after 3 seconds
                setTimeout(() => {
                    setSaveStatus('');
                }, 3000);

                return true; // Indicate success

            } catch (error) {
                trackEvent('profile_update_error', {
                    error: error.message,
                    userId
                });
                console.error('Error saving profile:', error);
                setSaveStatus('error');
                setIsSaving(false);

                // Reset error status after 5 seconds
                setTimeout(() => {
                    setSaveStatus('');
                }, 5000);

                throw error; // Re-throw to be caught by caller
            }
        };

        // Update parent state (causes re-render)
        const updateParentState = () => {
            setUserProfile(prev => ({
                ...prev,
                currentSituation: formData.currentSituation,
                shortTermGoals: formData.shortTermGoals,
                longTermGoals: formData.longTermGoals,
                websiteUrl: formData.websiteUrl,
                // Include subtitle colors in parent state update
                subtitleFontColor: subtitleFontColor,
                subtitleOutlineColor: subtitleOutlineColor,
                subtitleBackgroundColor: subtitleBackgroundColor,
                subtitleOutlineEnabled: subtitleOutlineEnabled,
                prospectingProfile: {
                    currentRole: formData.prospectingProfile.currentRole,
                    companyPurpose: formData.prospectingProfile.companyPurpose,
                    targetAudience: formData.prospectingProfile.targetAudience,
                    howToBePerceived: formData.prospectingProfile.howToBePerceived,
                    currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                    contentIdeas: formData.prospectingProfile.contentIdeas,
                    instagramUrl: formData.prospectingProfile.instagramUrl,
                    inspiringBrands: formData.prospectingProfile.inspiringBrands,
                    competitors: formData.prospectingProfile.competitors,
                    targetChallenge: formData.prospectingProfile.targetChallenge
                },
                socialMedia: formData.socialMedia,
                videoProduction: formData.videoProduction
            }));
        };

        // Main save function (backend only)
        const handleSave = async () => {
            return await saveToBackend();
        };

        const handleManageSubscription = async () => {
            try {
                const response = await api.createPortalSession();
                if (response.data.url) {
                    window.open(response.data.url, '_blank');
                }
            } catch (error) {
                console.error('Error opening subscription portal:', error);
                // Optionally show an error message to the user
            }
        };

        // Voice cloning useEffect hooks - moved inside ProfileModal
        // Initialize random examples when showing them
        useEffect(() => {
            if (showExamples) {
                // Shuffle array and take first 10 examples
                const shuffled = [...RECORDING_EXAMPLES]
                    .sort(() => Math.random() - 0.5)
                    .slice(0, 10);
                setDisplayedExamples(shuffled);
                setCurrentExample(0);
            }
        }, [showExamples]);

        // Rotate through examples
        useEffect(() => {
            if (showExamples && displayedExamples.length > 0) {
                const interval = setInterval(() => {
                    setCurrentExample(prev => (prev + 1) % displayedExamples.length);
                }, 3000);
                return () => clearInterval(interval);
            }
        }, [showExamples, displayedExamples]);

        // Fetch user voice data
        useEffect(() => {
            const fetchUserVoiceData = async () => {
                try {
                    const response = await api.getUserProfile();
                    console.log('Full user data:', response.data);
                    const userData = response.data;

                    if (userData.voiceId) {
                        console.log('Found voice ID:', userData.voiceId);
                        setUserVoiceId(userData.voiceId);
                        setShowRecordingSection(false);
                    } else {
                        console.log('No voice ID found in user data');
                        setShowRecordingSection(true);
                    }

                    if (response.data.recordings) {
                        setRecordings(response.data.recordings);
                        console.log('Recordings loaded:', response.data.recordings);
                    }

                    // Initialize test audio URL from user data
                    if (userData.testAudio) {
                        setTestAudioUrl(userData.testAudio);
                        console.log('Test audio URL loaded:', userData.testAudio);
                    }

                    console.log('User voice ID:', userData.voiceId);

                    trackEvent('user_voice_data_fetched', {
                        userId,
                        voiceId: userData.voiceId,
                        hasRecordings: response.data.recordings ? response.data.recordings.length > 0 : false,
                        hasTestAudio: !!userData.testAudio
                    });

                } catch (error) {
                    console.error('Error fetching user voice data:', error);
                }
            };

            // Always fetch when modal opens, not just when userId changes
            if (userId) {
                fetchUserVoiceData();
            }
        }, [userId]);

        // Voice cloning utility functions - moved inside ProfileModal
        const formatTime = (seconds) => {
            if (typeof seconds === 'string') {
                return seconds;
            }
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        // Initialize voice recorder
        const initializeRecorder = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setMediaStream(stream);

                const recorder = new MediaRecorder(stream, {
                    mimeType: 'audio/webm'
                });

                const chunks = [];
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                        chunks.push(e.data);
                    }
                };

                recorder.onstop = async () => {
                    const audioBlob = new Blob(chunks, { type: 'audio/webm' });
                    const finalDuration = recordingDuration; // Capture duration immediately
                    console.log('Final recording duration:', recordingDuration); // Should show actual duration
                    console.log('Final finalDuration:', finalDuration);

                    try {
                        // Create FormData and append both audio and duration
                        const formData = new FormData();
                        formData.append('audio', audioBlob, 'recording.webm');
                        formData.append('duration', finalDuration); // Use captured duration
                        console.log('FormData duration:', formData.get('duration'));

                        const response = await api.saveRecording(formData);
                        console.log('Recording saved:', response.data);
                        if (response.data.success) {
                            setRecordings(prev => [...prev, response.data.recording]);
                            // Store the duration in local state
                            setRecordingLength(prev => ({
                                ...prev,
                                [response.data.recording._id]: finalDuration
                            }));
                        }
                    } catch (error) {
                        console.error('Error saving recording:', error);
                    } finally {
                        setIsSavingRecording(false);
                        setIsRecording(false);
                    }
                    chunks.length = 0; // Clear the chunks array
                };

                setMediaRecorder(recorder);
                return recorder;
            } catch (error) {
                console.error('Error accessing microphone:', error);
                return null;
            }
        };

        // Start recording
        const startRecording = async () => {
            if (recordings.length >= 5) {
                setErrorMessage('Maximum number of recordings reached');
                setShowErrorModal(true);
                return;
            }

            try {
                setIsRecording(true);
                setIsSavingRecording(false);
                setRecordingDuration(0);

                const recorder = await initializeRecorder();
                if (!recorder) {
                    setIsRecording(false);
                    return;
                }

                recorder.start();
                setMediaRecorder(recorder);

                // Start timer
                const interval = setInterval(() => {
                    setRecordingDuration(prev => prev + 1);
                }, 1000);
                setTimerInterval(interval);

            } catch (error) {
                console.error('Error starting recording:', error);
                setIsRecording(false);
            }
        };

        // Stop recording
        const stopRecording = () => {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
                setIsSavingRecording(true);
                mediaRecorder.stop();

                // Stop timer
                if (timerInterval) {
                    clearInterval(timerInterval);
                    setTimerInterval(null);
                }

                // Stop all tracks
                if (mediaStream) {
                    mediaStream.getTracks().forEach(track => track.stop());
                }
            }
        };

        // Handle voice cloning
        const handleVoiceCloning = async () => {
            try {
                setIsGeneratingVoice(true);

                console.log('Recordings to send:', recordings);

                if (!recordings || recordings.length === 0) {
                    throw new Error('No recordings found. Please record some samples first.');
                }

                // Create FormData
                const formData = new FormData();

                // Append each recording URL as a separate field
                recordings.forEach((recording, index) => {
                    formData.append(`recording${index}`, recording.url);
                });

                // Add the total number of recordings
                formData.append('recordingCount', recordings.length);

                // For debugging - log the FormData contents
                for (let pair of formData.entries()) {
                    console.log(pair[0], pair[1]);
                }

                // Clone the voice using ElevenLabs API
                const cloneResponse = await api.cloneVoice(formData);

                console.log('Voice cloning response:', cloneResponse.data);

                if (cloneResponse.data && cloneResponse.data.voice_id) {
                    setUserVoiceId(cloneResponse.data.voice_id);
                    await api.updateProfile({ voiceId: cloneResponse.data.voice_id });
                    setSuccessMessage('Voice cloned successfully!');
                    setShowSuccessModal(true);
                }
                trackEvent('voice_cloning_completed', {
                    userId,
                    voiceId: cloneResponse.data.voice_id
                });
            } catch (error) {
                console.error('Error cloning voice:', error);
                setErrorMessage(error.message || 'Failed to clone voice. Please try again.');
                setShowErrorModal(true);
                trackEvent('voice_cloning_error', {
                    userId,
                    error: error.message
                });
            } finally {
                setIsGeneratingVoice(false);
            }
        };

        // Test cloned voice
        const testClonedVoice = async () => {
            if (!userVoiceId) {
                setErrorMessage('Please clone your voice first');
                setShowErrorModal(true);
                return;
            }

            try {
                setIsTestingVoice(true);
                const response = await api.testClonedVoice(
                    "hey, this is me, the cloned voice, do I sound like you ?",
                    userVoiceId
                );

                // The response will now contain the S3 URL
                if (response.data && response.data.audioUrl) {
                    setTestAudioUrl(response.data.audioUrl); // Update this line
                }
                trackEvent('voice_test_completed', {
                    userId,
                    voiceId: userVoiceId,
                    audioUrl: response.data.audioUrl
                });
            } catch (error) {
                console.error('Error testing cloned voice:', error);
                setErrorMessage('Failed to test voice. Please try again.');
                setShowErrorModal(true);
                trackEvent('voice_test_error', {
                    userId,
                    error: error.message
                });
            } finally {
                setIsTestingVoice(false);
            }
        };

        // Handle delete recording
        const handleDeleteRecording = async (recordingId) => {
            try {
                await api.deleteRecording(recordingId);
                setRecordings(prev => prev.filter(recording => recording._id !== recordingId));

                // Remove from recording length state
                setRecordingLength(prev => {
                    const newState = { ...prev };
                    delete newState[recordingId];
                    return newState;
                });
            } catch (error) {
                console.error('Error deleting recording:', error);
                setErrorMessage('Failed to delete recording. Please try again.');
                setShowErrorModal(true);
            }
        };

        const handleLogout = () => {
            // Clear local storage
            localStorage.removeItem('token');
            localStorage.removeItem('userId');

            // Track logout event
            trackEvent('user_logged_out', { userId });

            // Redirect to home
            window.location.href = '/';
        };

        const handleCloseAttempt = () => {
            // Check if there are unsaved changes
            if (checkForChanges()) {
                setShowSaveConfirmation(true);
            } else {
                // No changes, close directly
                handleDirectClose();
            }
        };

        const handleConfirmClose = () => {
            setShowSaveConfirmation(false);

            // If changes were saved to backend, update parent state before closing
            if (hasSavedToBackend) {
                updateParentState();
            }

            setShowProfileModal(false);
        };

        // Handle direct close (no confirmation needed)
        const handleDirectClose = () => {
            // If changes were saved to backend, update parent state before closing
            if (hasSavedToBackend) {
                updateParentState();
            }

            setShowProfileModal(false);
        };

        const handleSaveAndClose = async () => {
            try {
                setShowSaveConfirmation(false);

                // Save to backend first
                const saveSuccess = await saveToBackend();

                if (saveSuccess) {
                    // Update parent state and close modal
                    updateParentState();
                    setShowProfileModal(false);
                }
                // If save fails, the modal stays open and error is shown
            } catch (error) {
                console.error('Error saving and closing:', error);
                // Keep the modal open if save fails
            }
        };

        const handleCancelClose = () => {
            setShowSaveConfirmation(false);
        };

        // Check if there are unsaved changes
        const checkForChanges = () => {
            // Use savedFormData as baseline if available, otherwise use userProfile
            const baseline = savedFormData || {
                currentSituation: userProfile.currentSituation || '',
                shortTermGoals: userProfile.shortTermGoals || '',
                longTermGoals: userProfile.longTermGoals || '',
                websiteUrl: userProfile.websiteUrl || '',
                subtitleFontColor: userProfile.subtitleFontColor || '#ffffff',
                subtitleOutlineColor: userProfile.subtitleOutlineColor || '#000000',
                subtitleBackgroundColor: userProfile.subtitleBackgroundColor || 'transparent',
                subtitleOutlineEnabled: userProfile.subtitleOutlineEnabled !== undefined ? userProfile.subtitleOutlineEnabled : false,
                prospectingProfile: {
                    currentRole: userProfile.prospectingProfile?.currentRole || '',
                    companyPurpose: userProfile.prospectingProfile?.companyPurpose || '',
                    targetAudience: userProfile.prospectingProfile?.targetAudience || '',
                    howToBePerceived: userProfile.prospectingProfile?.howToBePerceived || '',
                    currentMarketingStrategy: userProfile.prospectingProfile?.currentMarketingStrategy || '',
                    contentIdeas: userProfile.prospectingProfile?.contentIdeas || '',
                    instagramUrl: userProfile.prospectingProfile?.instagramUrl || '',
                    inspiringBrands: userProfile.prospectingProfile?.inspiringBrands || '',
                    competitors: userProfile.prospectingProfile?.competitors || '',
                    targetChallenge: userProfile.prospectingProfile?.targetChallenge || ''
                },
                socialMedia: {
                    mainPlatform: userProfile.socialMedia?.mainPlatform || 'instagram',
                    instagram: {
                        handle: userProfile.socialMedia?.instagram?.handle || '',
                        currentFollowers: userProfile.socialMedia?.instagram?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.instagram?.targetFollowers || 0
                    },
                    tiktok: {
                        handle: userProfile.socialMedia?.tiktok?.handle || '',
                        currentFollowers: userProfile.socialMedia?.tiktok?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.tiktok?.targetFollowers || 0
                    },
                    youtube: {
                        handle: userProfile.socialMedia?.youtube?.handle || '',
                        currentFollowers: userProfile.socialMedia?.youtube?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.youtube?.targetFollowers || 0
                    }
                },
                videoProduction: {
                    currentVideosPerMonth: userProfile.videoProduction?.currentVideosPerMonth || 0,
                    targetVideosPerMonth: userProfile.videoProduction?.targetVideosPerMonth || 0
                }
            };

            const hasChanges =
                formData.currentSituation !== baseline.currentSituation ||
                formData.shortTermGoals !== baseline.shortTermGoals ||
                formData.longTermGoals !== baseline.longTermGoals ||
                formData.websiteUrl !== baseline.websiteUrl ||
                subtitleFontColor !== baseline.subtitleFontColor ||
                subtitleOutlineColor !== baseline.subtitleOutlineColor ||
                subtitleBackgroundColor !== baseline.subtitleBackgroundColor ||
                subtitleOutlineEnabled !== baseline.subtitleOutlineEnabled ||
                formData.prospectingProfile.currentRole !== baseline.prospectingProfile.currentRole ||
                formData.prospectingProfile.companyPurpose !== baseline.prospectingProfile.companyPurpose ||
                formData.prospectingProfile.targetAudience !== baseline.prospectingProfile.targetAudience ||
                formData.prospectingProfile.howToBePerceived !== baseline.prospectingProfile.howToBePerceived ||
                formData.prospectingProfile.currentMarketingStrategy !== baseline.prospectingProfile.currentMarketingStrategy ||
                formData.prospectingProfile.contentIdeas !== baseline.prospectingProfile.contentIdeas ||
                formData.prospectingProfile.instagramUrl !== baseline.prospectingProfile.instagramUrl ||
                formData.prospectingProfile.inspiringBrands !== baseline.prospectingProfile.inspiringBrands ||
                formData.prospectingProfile.competitors !== baseline.prospectingProfile.competitors ||
                formData.prospectingProfile.targetChallenge !== baseline.prospectingProfile.targetChallenge ||
                formData.socialMedia.mainPlatform !== baseline.socialMedia.mainPlatform ||
                formData.socialMedia.instagram.handle !== baseline.socialMedia.instagram.handle ||
                formData.socialMedia.instagram.currentFollowers !== baseline.socialMedia.instagram.currentFollowers ||
                formData.socialMedia.instagram.targetFollowers !== baseline.socialMedia.instagram.targetFollowers ||
                formData.socialMedia.tiktok.handle !== baseline.socialMedia.tiktok.handle ||
                formData.socialMedia.tiktok.currentFollowers !== baseline.socialMedia.tiktok.currentFollowers ||
                formData.socialMedia.tiktok.targetFollowers !== baseline.socialMedia.tiktok.targetFollowers ||
                formData.socialMedia.youtube.handle !== baseline.socialMedia.youtube.handle ||
                formData.socialMedia.youtube.currentFollowers !== baseline.socialMedia.youtube.currentFollowers ||
                formData.socialMedia.youtube.targetFollowers !== baseline.socialMedia.youtube.targetFollowers ||
                formData.videoProduction.currentVideosPerMonth !== baseline.videoProduction.currentVideosPerMonth ||
                formData.videoProduction.targetVideosPerMonth !== baseline.videoProduction.targetVideosPerMonth;

            setHasUnsavedChanges(hasChanges);
            return hasChanges;
        };

        // Update hasUnsavedChanges whenever formData or subtitle colors change
        useEffect(() => {
            const hasChanges = checkForChanges();

            // If there are new changes, reset the saved to backend flag
            if (hasChanges) {
                setHasSavedToBackend(false);
            }
        }, [formData, subtitleFontColor, subtitleOutlineColor, subtitleBackgroundColor, subtitleOutlineEnabled]);

        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'right',
                    alignItems: 'flex-start',
                    paddingTop: window.innerWidth <= 600 ? '20px' : '100px',
                    paddingRight: '20px',
                    paddingLeft: '20px',
                    paddingBottom: '100px',
                    zIndex: 10004
                }}
                onClick={handleCloseAttempt}
            >
                <div
                    style={{
                        backgroundColor: darkMode ? '#27292D' : '#ffffff',
                        padding: '30px',
                        borderRadius: '10px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        boxShadow: darkMode ? '0 18px 48px rgba(0,0,0,0.55)' : '0 18px 48px rgba(15,23,42,0.18)',
                        border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                        position: 'relative',
                        // maxHeight: '90vh',
                        // overflowY: 'auto',
                        // minHeight: '350px',
                        // maxHeight: '400px'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Rounded triangle decoration */}
                    <div
                        style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '20px',
                            width: '16px',
                            height: '16px',
                            backgroundColor: darkMode ? '#27292D' : '#ffffff',
                            transform: 'rotate(45deg)',
                            borderRadius: '2px',
                            borderLeft: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                            borderTop: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
                            // boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)'
                        }}
                    />
                    {/* Close button */}
                    <button
                        onClick={handleCloseAttempt}
                        style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: darkMode ? '#fff' : '#333',
                            padding: '5px',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.backgroundColor = darkMode ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.06)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                        }}
                    >
                        ×
                    </button>

                    {/* <h2 style={{ fontSize: window.innerWidth <= 600 ? '20px' : '24px', marginBottom: '24px', marginTop: 0 }}>
                        Hi {(userInfo.name)?.split(' ')[0]}!
                    </h2> */}

                    {/* {userId === BOUKAR_USER_ID && (
                        <div style={{ marginBottom: '20px' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowProfileModal(false);
                                    navigate('/workspace/covers');
                                }}
                                style={{
                                    padding: '10px 18px',
                                    borderRadius: '8px',
                                    border: darkMode ? '1px solid #52525b' : '1px solid #d4d4d8',
                                    backgroundColor: darkMode ? '#3f3f46' : '#f4f4f5',
                                    color: darkMode ? '#fafafa' : '#18181b',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                }}
                            >
                                Go to covers
                            </button>
                        </div>
                    )} */}

                    {/* Profile picture — centered, with change overlay */}
                    {/* <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
                        <input
                            type="file"
                            ref={profilePictureInputRef}
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            style={{ display: 'none' }}
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setProfilePictureError('');
                                setProfilePictureUploading(true);
                                try {
                                    const formData = new FormData();
                                    formData.append('picture', file);
                                    const res = await api.uploadProfilePicture(formData);
                                    if (res.data?.picture) {
                                        setUserProfile(prev => ({ ...prev, picture: res.data.picture }));
                                        setUserInfo(prev => ({ ...prev, picture: res.data.picture }));
                                    }
                                } catch (err) {
                                    setProfilePictureError(err.response?.data?.error || 'Failed to upload picture');
                                } finally {
                                    setProfilePictureUploading(false);
                                    e.target.value = '';
                                }
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => !profilePictureUploading && profilePictureInputRef.current?.click()}
                            disabled={profilePictureUploading}
                            style={{
                                position: 'relative',
                                padding: 0,
                                border: 'none',
                                background: 'none',
                                cursor: profilePictureUploading ? 'not-allowed' : 'pointer',
                                borderRadius: '50%',
                                outline: 'none',
                                boxShadow: darkMode ? '0 0 0 2px #444' : '0 0 0 2px #e0e0e0',
                                transition: 'box-shadow 0.2s ease, transform 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                if (profilePictureUploading) return;
                                e.currentTarget.style.boxShadow = darkMode ? '0 0 0 3px rgba(171, 88, 152, 0.70)' : '0 0 0 3px rgba(92, 75, 211, 0.55)';
                                e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.boxShadow = darkMode ? '0 0 0 2px #444' : '0 0 0 2px #e0e0e0';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            <div
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    backgroundColor: darkMode ? '#333' : '#e8e8e8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {userInfo.picture ? (
                                    <img
                                        src={userInfo.picture}
                                        alt="Profile"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <User size={32} strokeWidth={1.5} color={darkMode ? '#666' : '#999'} />
                                )}
                            </div>
                            {profilePictureUploading && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: '50%',
                                        background: 'rgba(0,0,0,0.5)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Loader size={24} color="#fff" style={{ animation: 'spin 0.8s linear infinite' }} />
                                </div>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => !profilePictureUploading && profilePictureInputRef.current?.click()}
                            disabled={profilePictureUploading}
                            style={{
                                marginTop: '12px',
                                padding: '6px 12px',
                                border: 'none',
                                background: 'transparent',
                                color: '#AB5898',
                                fontSize: '13px',
                                fontWeight: 500,
                                cursor: profilePictureUploading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                opacity: profilePictureUploading ? 0.6 : 1,
                                transition: 'opacity 0.2s'
                            }}
                        >
                            <Camera size={14} strokeWidth={2} />
                            {profilePictureUploading ? 'Uploading…' : 'Change photo'}
                        </button>
                        {profilePictureError && (
                            <p style={{ marginTop: '8px', color: '#e57373', fontSize: '12px', textAlign: 'center' }}>{profilePictureError}</p>
                        )}
                    </div> */}

                    <div>
                        <h2 style={{ marginBottom: '12px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: darkMode ? '#888' : '#999', marginTop: '32px' }}>Legal & Privacy</h2>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <Link
                                to="/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: 'var(--text-color)',
                                    textDecoration: 'none',
                                    padding: '10px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--input-border)',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    background: darkMode ? '#1A202C' : 'transparent'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--background-tertiary)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = darkMode ? '#1A202C' : 'transparent';
                                }}
                            >
                                Privacy Policy
                            </Link>
                            <Link
                                to="/terms-of-service"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: 'var(--text-color)',
                                    textDecoration: 'none',
                                    padding: '10px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--input-border)',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    background: darkMode ? '#1A202C' : 'transparent'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--background-tertiary)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = darkMode ? '#1A202C' : 'transparent';
                                }}
                            >
                                Terms of Service
                            </Link>
                            <Link
                                to="/confidentiality-rules"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: 'var(--text-color)',
                                    textDecoration: 'none',
                                    padding: '10px 14px',
                                    borderRadius: '8px',
                                    border: '1px solid var(--input-border)',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    transition: 'all 0.2s ease',
                                    background: darkMode ? '#1A202C' : 'transparent'
                                }}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--background-tertiary)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = darkMode ? '#1A202C' : 'transparent';
                                }}
                            >
                                Confidentiality Rules
                            </Link>
                        </div>

                        <h2 style={{ marginBottom: '12px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: darkMode ? '#888' : '#999', marginTop: '32px' }}>Contact</h2>
                        <p style={{ marginBottom: '16px', fontSize: '13px', color: darkMode ? '#aaa' : '#666', lineHeight: 1.4 }}>
                            Support, bug reports, feature requests
                        </p>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0,
                                marginBottom: '24px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: '1px solid var(--input-border)',
                                background: darkMode ? '#1A202C' : 'transparent'
                            }}
                        >
                            <a
                                href="mailto:hello@merra.ai"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '14px',
                                    padding: '14px 16px',
                                    color: 'var(--text-color)',
                                    textDecoration: 'none',
                                    borderBottom: '1px solid var(--input-border)',
                                    transition: 'background 0.15s ease',
                                    background: darkMode ? '#1A202C' : 'transparent'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--background-tertiary)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = darkMode ? '#1A202C' : 'transparent'; }}
                            >
                                <div style={{ width: 36, height: 36, borderRadius: '8px', background: darkMode ? '#4A9EFF22' : '#1976d218', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Mail size={18} strokeWidth={2} color={darkMode ? '#4A9EFF' : '#1976d2'} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '11px', fontWeight: 600, color: darkMode ? '#888' : '#999', marginBottom: '2px' }}>Email</div>
                                    <div style={{ fontSize: '14px', fontWeight: 500 }}>hello@merra.ai</div>
                                </div>
                                <ExternalLink size={14} color={darkMode ? '#777' : '#999'} style={{ flexShrink: 0 }} />
                            </a>
                            <a
                                href="https://wa.me/33669773161"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '14px',
                                    padding: '14px 16px',
                                    color: 'var(--text-color)',
                                    textDecoration: 'none',
                                    borderBottom: 'none',
                                    transition: 'background 0.15s ease',
                                    background: darkMode ? '#1A202C' : 'transparent'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = 'var(--background-tertiary)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = darkMode ? '#1A202C' : 'transparent'; }}
                            >
                                <div style={{ width: 36, height: 36, borderRadius: '10px', background: darkMode ? 'rgba(37,211,102,0.12)' : 'rgba(37,211,102,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(37,211,102,0.18)' }}>
                                    <MessageCircle size={18} strokeWidth={2} color="#25D366" />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '11px', fontWeight: 600, color: darkMode ? '#888' : '#999', marginBottom: '2px' }}>WhatsApp</div>
                                    <div style={{ fontSize: '14px', fontWeight: 500 }}>+33 6 69 77 31 61 (Boukar Sall, cofounder of Merra)</div>
                                </div>
                                <ExternalLink size={14} color={darkMode ? '#777' : '#999'} style={{ flexShrink: 0 }} />
                            </a>
                            {/* <a
                                href="https://x.com/boukar_sall"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '14px',
                                    padding: '14px 16px',
                                    color: darkMode ? '#e0e0e0' : '#333',
                                    textDecoration: 'none',
                                    transition: 'background 0.15s ease'
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ width: 36, height: 36, borderRadius: '8px', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <span style={{ fontSize: '16px', fontWeight: 700, color: darkMode ? '#e0e0e0' : '#333' }}>𝕏</span>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '11px', fontWeight: 600, color: darkMode ? '#888' : '#999', marginBottom: '2px' }}>X</div>
                                    <div style={{ fontSize: '14px', fontWeight: 500 }}>@boukarsall</div>
                                </div>
                                <ExternalLink size={14} color={darkMode ? '#666' : '#bbb'} style={{ flexShrink: 0 }} />
                            </a> */}
                        </div>

                        <button
                        onClick={handleLogout}
                        style={{
                            padding: '10px 16px',
                            borderRadius: '8px',
                            border: darkMode ? '1px solid #4a2a2a' : '1px solid #f0d0d0',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#c62828',
                            fontSize: '14px',
                            fontWeight: 500,
                            background: darkMode ? 'rgba(198, 40, 40, 0.12)' : 'rgba(198, 40, 40, 0.06)',
                            transition: 'background 0.2s ease, border-color 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = darkMode ? 'rgba(198, 40, 40, 0.2)' : 'rgba(198, 40, 40, 0.1)';
                            e.currentTarget.style.borderColor = '#c62828';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = darkMode ? 'rgba(198, 40, 40, 0.12)' : 'rgba(198, 40, 40, 0.06)';
                            e.currentTarget.style.borderColor = darkMode ? '#4a2a2a' : '#f0d0d0';
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Logout
                    </button>


                        <button
                            onClick={handleCloseAttempt}
                            style={{
                                padding: '12px 20px',
                                borderRadius: '8px',
                                border: darkMode ? '1px solid #444' : '1px solid #e0e0e0',
                                cursor: 'pointer',
                                width: '100%',
                                marginTop: '32px',
                                fontSize: '14px',
                                fontWeight: 500,
                                background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                color: darkMode ? '#e0e0e0' : '#333',
                                transition: 'background 0.2s ease, border-color 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
                                e.currentTarget.style.borderColor = darkMode ? '#555' : '#ccc';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                                e.currentTarget.style.borderColor = darkMode ? '#444' : '#e0e0e0';
                            }}
                        >
                            Close
                        </button>
                    </div>
                    {/* {userProfile.isSubscribed && (
                        <button
                            onClick={handleManageSubscription}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '5px',
                                // backgroundColor: '#f0f0f0',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginTop: '10px'
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 10h18M7 15h.01M11 15h.01M15 15h.01M19 15h.01M5 20h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"/>
                            </svg>
                            Manage Subscription
                        </button>
                    )} */}
                    {/* <h2>Tell us more about you</h2>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                        This information helps us provide better personalized assistance
                    </p>
                    {!hasVisited && (
                        <p style={{ color: '#1976d2', marginBottom: '20px' }}>
                            You can change it whenever.
                        </p>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Bio
                        </label>
                        <TextareaAutosize
                            value={formData.currentSituation}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                currentSituation: e.target.value
                            }))}
                            placeholder="Name (optional), experience, skills, background, etc..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div> */}

                    {/*<div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Short Term Goals
                        </label>
                        <TextareaAutosize
                            value={formData.shortTermGoals}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shortTermGoals: e.target.value
                            }))}
                            placeholder="Goals for the next 3-6 months..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Long Term Goals
                        </label>
                        <TextareaAutosize
                            value={formData.longTermGoals}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                longTermGoals: e.target.value
                            }))}
                            placeholder="Where do you see yourself in 1-5 years..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>
                    */}

                    {/* <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Website URL
                        </label>
                        <input
                            type="url"
                            value={formData.websiteUrl}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                websiteUrl: e.target.value
                            }))}
                            placeholder="https://your-website.com"
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <h3 style={{ 
                        marginTop: '30px', 
                        marginBottom: '20px', 
                        color: darkMode ? '#fff' : '#333',
                        borderBottom: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                        paddingBottom: '10px'
                    }}>
                        Professional Profile
                    </h3> */}

                    {/* <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Current Role
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.currentRole}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    currentRole: e.target.value
                                }
                            }))}
                            placeholder="Your current role..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div> */}

                    {/* <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Company Purpose
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.companyPurpose}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    companyPurpose: e.target.value
                                }
                            }))}
                            placeholder="Your company's purpose..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Target Audience
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.targetAudience}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    targetAudience: e.target.value
                                }
                            }))}
                            placeholder="Your target audience..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            How do you want to be perceived?
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.howToBePerceived}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    howToBePerceived: e.target.value
                                }
                            }))}
                            placeholder="How do you want your audience to perceive you and your brand..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Current Marketing Strategy
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.currentMarketingStrategy}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    currentMarketingStrategy: e.target.value
                                }
                            }))}
                            placeholder="Describe your current marketing approach and strategies..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Content Ideas
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.contentIdeas}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    contentIdeas: e.target.value
                                }
                            }))}
                            placeholder="What types of content do you want to create? Any specific ideas or themes..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div> */}

                    {/*
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Link to your Instagram
                        </label>
                        <input
                            type="url"
                            value={formData.prospectingProfile.instagramUrl}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    instagramUrl: e.target.value
                                }
                            }))}
                            placeholder="https://instagram.com/yourusername"
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                color: darkMode ? '#fff' : '#333',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    */}

                    {/* <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Brands / Accounts that inspire you
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.inspiringBrands}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    inspiringBrands: e.target.value
                                }
                            }))}
                            placeholder="List brands, influencers, or accounts that inspire your content and business approach..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Competitors and main differentiation
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.competitors}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    competitors: e.target.value
                                }
                            }))}
                            placeholder="Who are your main competitors and what makes you different from them..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Your target's main challenges and how you solve them
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.targetChallenge}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    targetChallenge: e.target.value
                                }
                            }))}
                            placeholder="What are the main challenges your target audience faces and how do you help solve them..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <h3 style={{ 
                        marginTop: '30px', 
                        marginBottom: '20px', 
                        color: darkMode ? '#fff' : '#333',
                        borderBottom: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                        paddingBottom: '10px'
                    }}>
                        Social Media & Goals
                    </h3> */}

                    {/* Main Platform Selection */}
                    {/* <div style={{ marginBottom: '25px' }}>
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '10px', 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: darkMode ? '#fff' : '#333'
                        }}>Main Social Media Platform</label>
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <div
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        socialMedia: {
                                            ...prev.socialMedia,
                                            mainPlatform: 'instagram'
                                        }
                                    }))}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {formData.socialMedia.mainPlatform === 'instagram' && (
                                        <div style={{
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                        }} />
                                    )}
                                </div>
                                <span style={{ 
                                    fontSize: '14px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>Instagram</span>
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <div
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        socialMedia: {
                                            ...prev.socialMedia,
                                            mainPlatform: 'tiktok'
                                        }
                                    }))}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {formData.socialMedia.mainPlatform === 'tiktok' && (
                                        <div style={{
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                        }} />
                                    )}
                                </div>
                                <span style={{ 
                                    fontSize: '14px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>TikTok</span>
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <div
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        socialMedia: {
                                            ...prev.socialMedia,
                                            mainPlatform: 'youtube'
                                        }
                                    }))}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {formData.socialMedia.mainPlatform === 'youtube' && (
                                        <div style={{
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                        }} />
                                    )}
                                </div>
                                <span style={{ 
                                    fontSize: '14px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>YouTube</span>
                            </label>
                        </div>
                    </div> */}

                    {/* Instagram */}
                    {/* <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>Instagram</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Handle</label>
                            <input
                                type="text"
                                placeholder="@username"
                                value={formData.socialMedia.instagram.handle}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        instagram: {
                                            ...prev.socialMedia.instagram,
                                            handle: e.target.value
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                            <input
                                value={formData.socialMedia.instagram.currentFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        instagram: {
                                            ...prev.socialMedia.instagram,
                                            currentFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                            <input
                                value={formData.socialMedia.instagram.targetFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        instagram: {
                                            ...prev.socialMedia.instagram,
                                            targetFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div> */}

                    {/* TikTok */}
                    {/* <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>TikTok</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Handle</label>
                            <input
                                type="text"
                                placeholder="@username"
                                value={formData.socialMedia.tiktok.handle}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        tiktok: {
                                            ...prev.socialMedia.tiktok,
                                            handle: e.target.value
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                            <input
                                value={formData.socialMedia.tiktok.currentFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        tiktok: {
                                            ...prev.socialMedia.tiktok,
                                            currentFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                            <input
                                value={formData.socialMedia.tiktok.targetFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        tiktok: {
                                            ...prev.socialMedia.tiktok,
                                            targetFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div> */}

                    {/* YouTube */}
                    {/* <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>YouTube</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Handle</label>
                            <input
                                type="text"
                                placeholder="@username"
                                value={formData.socialMedia.youtube.handle}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        youtube: {
                                            ...prev.socialMedia.youtube,
                                            handle: e.target.value
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                            <input
                                value={formData.socialMedia.youtube.currentFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        youtube: {
                                            ...prev.socialMedia.youtube,
                                            currentFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                            <input
                                value={formData.socialMedia.youtube.targetFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        youtube: {
                                            ...prev.socialMedia.youtube,
                                            targetFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div> */}

                    {/* Video Production */}
                    {/* <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>Video Production Goals</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '30px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Videos Per Month</label>
                            <input
                                value={formData.videoProduction.currentVideosPerMonth}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    videoProduction: {
                                        ...prev.videoProduction,
                                        currentVideosPerMonth: parseInt(e.target.value) || 0
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Videos Per Month</label>
                            <input
                                value={formData.videoProduction.targetVideosPerMonth}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    videoProduction: {
                                        ...prev.videoProduction,
                                        targetVideosPerMonth: parseInt(e.target.value) || 0
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    <h3 style={{ 
                        marginTop: '30px', 
                        marginBottom: '20px', 
                        color: darkMode ? '#fff' : '#333',
                        borderBottom: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                        paddingBottom: '10px'
                    }}>
                        Voice Cloning
                    </h3>

                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ color: '#666', marginBottom: '20px' }}>
                            Record your voice to create a digital voice clone.
                        </p>
                        <button
                            onClick={() => setShowRecordingSection(!showRecordingSection)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                borderRadius: '4px',
                                color: darkMode ? '#ffffff95' : '#666',
                                cursor: 'pointer',
                                marginBottom: '20px',
                                width: '180px'
                            }}
                        >
                            {showRecordingSection ? (
                                <>
                                    <ChevronUp size={16} />
                                    Hide Voice Cloning
                                </>
                            ) : (
                                <>
                                    <ChevronDown size={16} />
                                    Show Voice Cloning
                                </>
                            )}
                        </button>
                        {showRecordingSection && (
                        <div>
                        <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '10px' 
                        }}>
                            
                            <p style={{ fontSize:'14px', marginBottom: '10px', marginTop:'0px', color: '#666' }}>
                                Record at least 1 sample to clone your voice. 
                                For best results, record 3-5 samples, each 10-30 seconds long.
                            </p>
                            <button
                                onClick={() => setShowExamples(!showExamples)}
                                style={{
                                    padding: '2px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#1a73e8',
                                    border: '1px solid #1a73e8',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    marginLeft: '16px',
                                }}
                            >
                                {showExamples ? 'Hide inspiration' : 'Click for inspiration'}
                            </button>
                        </div> */}

                    {/* Example Sentences */}
                    {/* {showExamples ? (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                marginBottom: '16px',
                                border: '1px solid #dfe1e5',
                                position: 'relative',
                                overflow: 'hidden',
                                height: '60px' // Fixed height to prevent layout shifts
                            }}>
                                {displayedExamples.map((example, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '12px',
                                            right: '12px',
                                            transform: `translateY(-50%) translateY(${(index - currentExample) * 60}px)`,
                                            opacity: index === currentExample ? 1 : 0,
                                            transition: 'all 0.5s ease-in-out',
                                            textAlign: 'center',
                                            color: '#666'
                                        }}
                                    >
                                        "{example}"
                                    </div>
                                ))}
                            </div>
                        ):(
                            <p style={{fontSize:'14px', marginTop:'0px', marginBottom:'30px', color:'#666'}}>Not sure what to say? Click for inspiration.</p>
                        )} */}

                    {/* Progress Bar */}
                    {/* <div style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                                fontSize: '0.9em',
                                color: '#666'
                            }}>
                                <span>Recording Progress</span>
                                <span>{recordings.length} recording{recordings.length !== 1 ? 's' : ''} {recordings.length >= 1 ? '(Ready to clone)' : '(Need at least 1)'}</span>
                            </div>
                            <div style={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${Math.min((recordings.length / 5) * 100, 100)}%`,
                                    height: '100%',
                                    background: 'linear-gradient(to right, #AB5898 50%, #5C4BD3 80%)',
                                    transition: 'width 0.3s ease',
                                    borderRadius: '4px',
                                }} />
                            </div>
                            {recordings.length < 1 && (
                                <p style={{ 
                                    fontSize: '0.8em', 
                                    color: '#666', 
                                    marginTop: '8px' 
                                }}>
                                    At least 1 recording needed to clone your voice
                                </p>
                            )}
                            {recordings.length >= 1 && recordings.length < 5 && (
                                <p style={{ 
                                    fontSize: '0.8em', 
                                    color: '#666', 
                                    marginTop: '8px' 
                                }}>
                                    {5 - recordings.length} more {5 - recordings.length === 1 ? 'recording' : 'recordings'} for best results
                                </p>
                            )}
                        </div> */}

                    {/* Recording controls */}
                    {/* <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                            <button
                                onClick={isRecording ? stopRecording : startRecording}
                                className={`main-button ${isRecording ? 'recording' : ''}`}
                                style={{ 
                                    flex: 1,
                                    backgroundColor: (recordings.length >= 5 ? '#d9df70' : (isRecording ? (isSavingRecording ? '#ccc' : '#dc3545') : '#e1f679')),
                                    cursor: recordings.length >= 5 ? 'not-allowed' : 'pointer'
                                }}
                                disabled={recordings.length >= 5 && !isRecording}
                            >
                                {isRecording ? (
                                    <>
                                        {isSavingRecording ? <Loader size={16} style={{ marginRight: '5px' }} /> : <Square size={16} style={{ marginRight: '5px' }} />}
                                        {isSavingRecording ? 'Saving...' : <>'Stop Recording' ({formatTime(recordingDuration)})</>}
                                    </>
                                ) : (
                                    <>
                                        {recordings.length >=5 ? <Check size={16} style={{ marginRight: '5px' }} /> : <Mic size={16} style={{ marginRight: '5px' }} />}
                                        {recordings.length >= 5 ? 'Max Recordings Reached' : 'Start Recording'}
                                    </>
                                )}
                            </button>
                        </div> */}

                    {/* Recording list */}
                    {/* <div style={{ marginBottom: '20px' }}>
                            {recordings.map((recording, index) => (
                                <div 
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                        padding: '8px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                        border: '1px solid #dfe1e5'
                                    }}
                                >
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        marginBottom: '4px'
                                    }}>
                                        <span style={{ 
                                            color: '#666',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}>
                                            Sample {index + 1} ({recordingLength[recording._id] ? 
                                                formatTime(recordingLength[recording._id]) : 
                                                (recording.duration ? formatTime(recording.duration) : '')
                                            })
                                        </span>
                                        <button
                                            onClick={() => handleDeleteRecording(recording._id)}
                                            className='red-delete-button'
                                            style={{
                                                padding: '4px 8px',
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: '0.8em'
                                            }}
                                        >
                                            <Trash2 size={14} />
                                            Delete
                                        </button>
                                    </div> */}

                    {/* Simple audio player as fallback */}
                    {/* <audio 
                                        controls
                                        style={{ width: '100%', height: '12px' }}
                                        controlsList="nodownload"
                                        onError={(e) => {
                                            console.error('Audio playback error:', e.target.error);
                                            console.log('Audio URL:', recording.url);
                                            // Try to fetch the audio file directly to check for CORS or other issues
                                            fetch(recording.url)
                                                .then(response => {
                                                    console.log('Audio file response:', response);
                                                    return response.blob();
                                                })
                                                .then(blob => console.log('Audio blob:', blob))
                                                .catch(error => console.error('Error fetching audio:', error));
                                        }}
                                        onLoadStart={() => console.log('Audio loading started')}
                                        onLoadedMetadata={(e) => {
                                            // Format duration when metadata is loaded
                                            console.log('Audio metadata loaded, duration:', e.target.duration);
                                            if (isFinite(e.target.duration)) {
                                                const totalSeconds = Math.floor(e.target.duration);
                                                const minutes = Math.floor(totalSeconds / 60);
                                                const seconds = totalSeconds % 60;
                                                setRecordingLength(prev => ({
                                                    ...prev,
                                                    [recording._id]: `${minutes}:${seconds.toString().padStart(2, '0')}`
                                                }));
                                            }
                                        }}
                                        onLoadedData={() => console.log('Audio data loaded')}
                                    >
                                        <source 
                                            src={recording.url} 
                                            type="audio/webm"
                                            onError={(e) => console.error('Source error:', e)}
                                        />
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            ))}
                        </div> */}

                    {/* Clone voice button */}
                    {/* {recordings.length >= 1 && (
                            <button
                                onClick={handleVoiceCloning}
                                className="main-button"
                                disabled={isGeneratingVoice || recordings.length < 1}
                                style={{ width: '100%' }}
                            >
                                {isGeneratingVoice ? (
                                    <>
                                        <RefreshCw className="spin" size={16} style={{marginRight:'5px'}}/>
                                        Generating Voice Clone...
                                    </>
                                ) : (
                                    'Create My AI Voice'
                                )}
                            </button>
                        )}

                    </div>

                    )} */}

                    {/* Test cloned voice */}
                    {/* {userVoiceId && (
                        <div>
                            <h4 style={{color: darkMode ? "white" : "#333"}}>Test Your AI Voice</h4>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={testClonedVoice}
                                    className="main-button"
                                    disabled={isTestingVoice || !userVoiceId}
                                    style={{ flex: 1 }}
                                >
                                    {isTestingVoice? (
                                        <>
                                            <RefreshCw className="spin" size={16} style={{marginRight:'5px'}}/>
                                            Generating...
                                        </>
                                    ) : (
                                        'Generate Test'
                                    )}
                                </button>
                                {testAudioUrl && (
                                    <button
                                        onClick={() => {
                                            if (audioRef.current) {
                                                if (isPlayingVoice) {
                                                    audioRef.current.pause();
                                                } else {
                                                    audioRef.current.play();
                                                }
                                                setIsPlayingVoice(!isPlayingVoice);
                                            }
                                        }}
                                        className="main-button"
                                        style={{ width: '50px' }}
                                    >
                                        {isPlayingVoice ? (
                                            <Pause size={16} />
                                        ) : (
                                            <Play size={16} />
                                        )}
                                    </button>
                                )}
                            </div>
                            <audio
                                ref={audioRef}
                                src={testAudioUrl}
                                onEnded={() => setIsPlayingVoice(false)}
                                style={{ display: 'none' }}
                            />
                        </div>
                    )}

                    <h3 style={{ 
                        marginTop: '30px', 
                        marginBottom: '10px', 
                        color: darkMode ? '#fff' : '#333'
                    }}>Subtitle Style</h3> */}

                    {/* Outline Enabled Checkbox */}
                    {/* <div style={{ marginBottom: '15px' }}>
                        <label style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: darkMode ? '#fff' : '#333'
                        }}>
                            <input
                                type="checkbox"
                                checked={subtitleOutlineEnabled}
                                onChange={(e) => setSubtitleOutlineEnabled(e.target.checked)}
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer'
                                }}
                            />
                            Enable outline (recommended for readability)
                        </label>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '10px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Font Color</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }} data-font-color-picker>
                                <input
                                    type="text"
                                    value={subtitleFontColor}
                                    onChange={(e) => setSubtitleFontColor(e.target.value)}
                                    placeholder="#ffffff"
                                    style={{ 
                                        width: '70px', 
                                        padding: '10px', 
                                        borderRadius: '5px', 
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', 
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff', 
                                        color: darkMode ? '#fff' : '#333' 
                                    }}
                                />
                                <input
                                    type="color"
                                    value={subtitleFontColor}
                                    onChange={(e) => setSubtitleFontColor(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                    }}
                                    // autoFocus
                                />
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="color"
                                        value={subtitleFontColor}
                                        onChange={(e) => setSubtitleFontColor(e.target.value)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '40px',
                                            height: '40px',
                                            border: 'none',
                                            borderRadius: '3px',
                                            cursor: 'pointer',
                                            opacity: 0,
                                            zIndex: 1
                                        }}
                                        ref={(input) => {
                                            if (input) {
                                                input.click = () => {
                                                    input.click();
                                                };
                                            }
                                        }}
                                    />
                                    <img
                                        src={'/color-picker.png'}
                                        alt="Color Picker"
                                        style={{ 
                                            width: '30px', 
                                            height: '30px', 
                                            cursor: 'pointer',
                                            position: 'relative',
                                            zIndex: 0
                                        }}
                                        onClick={() => {
                                            const colorInput = document.querySelector('input[type="color"][value="' + subtitleFontColor + '"]');
                                            if (colorInput) {
                                                colorInput.click();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Outline Color</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }} data-outline-color-picker>
                                <input
                                    type="text"
                                    value={subtitleOutlineColor}
                                    onChange={(e) => setSubtitleOutlineColor(e.target.value)}
                                    placeholder="#000000"
                                    style={{ 
                                        width: '70px', 
                                        padding: '10px', 
                                        borderRadius: '5px', 
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', 
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff', 
                                        color: darkMode ? '#fff' : '#333' 
                                    }}
                                />
                                <input
                                    type="color"
                                    value={subtitleOutlineColor}
                                    onChange={(e) => setSubtitleOutlineColor(e.target.value)}
                                    style={{
                                        width: '100%',
                                        height: '40px',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                    }}
                                    // autoFocus
                                />
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="color"
                                        value={subtitleOutlineColor}
                                        onChange={(e) => setSubtitleOutlineColor(e.target.value)}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '40px',
                                            height: '40px',
                                            border: 'none',
                                            borderRadius: '3px',
                                            cursor: 'pointer',
                                            opacity: 0,
                                            zIndex: 1
                                        }}
                                        ref={(input) => {
                                            if (input) {
                                                input.click = () => {
                                                    input.click();
                                                };
                                            }
                                        }}
                                    />
                                    <img
                                        src={'/color-picker.png'}
                                        alt="Color Picker"
                                        style={{ 
                                            width: '30px', 
                                            height: '30px', 
                                            cursor: 'pointer',
                                            position: 'relative',
                                            zIndex: 0,
                                        }}
                                        onClick={() => {
                                            const colorInput = document.querySelector('input[type="color"][value="' + subtitleOutlineColor + '"]');
                                            if (colorInput) {
                                                colorInput.click();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div> */}
                    {/* <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Background (HEX or 'transparent')</label>
                            <input
                                type="text"
                                value={subtitleBackgroundColor}
                                onChange={(e) => setSubtitleBackgroundColor(e.target.value)}
                                placeholder="transparent"
                                style={{ width: '70px', padding: '10px', borderRadius: '5px', border: darkMode ? '1px solid #444' : '1px solid #dfe1e5', backgroundColor: darkMode ? '#2A2B2F' : '#fff', color: darkMode ? '#fff' : '#333' }}
                            />
                        </div> */}
                    {/* </div>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px' }}>Preview</label>
                        <div style={{
                            width: '100%',
                            height: '200px',
                            borderRadius: '8px',
                            background: '#000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative'
                        }}>
                            <img src={'https://cdn.fstoppers.com/styles/full/s3/media/2019/12/04/nando-jpeg-quality-050.jpg'} alt="Subtitle Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5, borderRadius: '8px' }} />
                            <div style={{
                                position: 'absolute',
                                bottom: '30px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                padding: subtitleBackgroundColor && subtitleBackgroundColor.toLowerCase() !== 'transparent' ? '6px 10px' : '0px',
                                backgroundColor: subtitleBackgroundColor && subtitleBackgroundColor.toLowerCase() !== 'transparent' ? `${subtitleBackgroundColor}99` : 'transparent',
                                color: subtitleFontColor,
                                fontWeight: 700,
                                fontSize: '20px',
                                textShadow: subtitleOutlineEnabled ? `-1px -1px 0 ${subtitleOutlineColor}, 1px -1px 0 ${subtitleOutlineColor}, -1px 1px 0 ${subtitleOutlineColor}, 1px 1px 0 ${subtitleOutlineColor}` : 'none',
                                borderRadius: '4px'
                            }}>Subtitle preview text</div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                        <button
                            onClick={handleCloseAttempt}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                cursor: 'pointer'
                            }}
                        >
                            Close
                        </button>
                        <button
                            onClick={handleSave}
                            className="main-button"
                            disabled={isSaving}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: '120px',
                                gap: '8px',
                                backgroundColor: saveStatus === 'saved' ? '#4CAF50' : saveStatus === 'error' ? '#f44336' : undefined,
                                color: saveStatus === 'saved' || saveStatus === 'error' ? 'white' : undefined,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border" style={{marginRight:'5px'}} />
                                    Saving...
                                </>
                            ) : saveStatus === 'saved' ? (
                                <>
                                    <Check size={16} />
                                    Saved
                                </>
                            ) : saveStatus === 'error' ? (
                                <>
                                    <AlertCircle size={16} />
                                    Error
                                </>
                            ) : (
                                'Save Profile'
                            )}
                        </button>
                    </div>
                    
                </div> */}

                    {/* Save Changes Confirmation Popup */}
                    {showSaveConfirmation && (
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 3000
                            }}
                            onClick={handleCancelClose}
                        >
                            <div
                                style={{
                                    backgroundColor: darkMode ? '#27292D' : 'white',
                                    padding: '30px',
                                    borderRadius: '10px',
                                    width: '90%',
                                    maxWidth: '400px',
                                    textAlign: 'center'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <h3 style={{ marginBottom: '20px', color: darkMode ? '#fff' : '#333' }}>
                                    Save Changes?
                                </h3>
                                <p style={{
                                    marginBottom: '25px',
                                    color: '#666',
                                    lineHeight: '1.5'
                                }}>
                                    You have unsaved changes. Do you want to save them before closing?
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <button
                                        onClick={handleCancelClose}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '5px',
                                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            color: darkMode ? '#fff' : '#333'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSaveAndClose}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '5px',
                                            border: 'none',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Save & Close
                                    </button>
                                    <button
                                        onClick={handleConfirmClose}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '5px',
                                            border: 'none',
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Close Without Saving
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const [isMuted, setIsMuted] = useState(() => {
        const savedMuteState = localStorage.getItem('musicMuted');
        return savedMuteState ? JSON.parse(savedMuteState) : false;
    });

    // Create audio element with the saved mute state
    const [audio] = useState(() => {
        const audioElement = new Audio('/Girl from Petaluma-2.mp3');
        audioElement.volume = 0.5; // Set volume to 50%
        audioElement.loop = true; // Make it loop

        // Apply the initial mute state
        const savedMuteState = localStorage.getItem('musicMuted');
        audioElement.muted = savedMuteState ? JSON.parse(savedMuteState) : false;

        return audioElement;
    });

    useEffect(() => {
        if (analyzing) {
            audio.play().catch(error => {
                console.error('Audio playback failed:', error);
            });
        } else {
            audio.pause();
            audio.currentTime = 0;
        }

        // Cleanup function
        return () => {
            audio.pause();
            audio.currentTime = 0;
        };
    }, [analyzing, audio]);

    // Effect to update localStorage when mute state changes
    useEffect(() => {
        localStorage.setItem('musicMuted', JSON.stringify(isMuted));
        audio.muted = isMuted;
    }, [isMuted, audio]);

    // Toggle mute function
    const toggleMute = () => {
        setIsMuted(prevMuted => !prevMuted);
    };




    const calculateYearProgress = () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 0); // Début de l'année
        const diff = now - start;
        const oneDay = 1000 * 60 * 60 * 24;
        const dayOfYear = Math.floor(diff / oneDay);
        const progress = (dayOfYear / 365) * 100;
        return progress.toFixed(1); // Retourne le pourcentage avec 1 décimale
    };

    const getDaysRemainingInYear = () => {
        const now = new Date();
        const endOfYear = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
        const diff = endOfYear - now;
        const oneDay = 1000 * 60 * 60 * 24;
        const daysRemaining = Math.ceil(diff / oneDay);
        return daysRemaining;
    };

    const handleDownloadVideo = async () => {
        console.log('handleDownloadVideo called');
        try {
            // Show a loading message
            setMessage('Downloading video...');
            setMessageType('info')
            console.log('Downloading video...');

            // Get the video element and its source
            const videoElement = document.querySelector('.result-container video');
            console.log('videoElement:', videoElement);

            // Get the source element inside the video
            const sourceElement = videoElement?.querySelector('source');
            const videoSrc = sourceElement?.src || videoElement?.src || generatedVideoUrl;

            console.log('videoSrc:', videoSrc);

            if (!videoSrc) {
                setMessage('Video source not found');
                setMessageType('error')
                return;
            }

            // Create a blob from the video source
            const response = await fetch(videoSrc);
            console.log('response:', response);
            const blob = await response.blob();
            console.log('blob:', blob);

            // Create a URL for the blob
            const blobUrl = URL.createObjectURL(blob);
            console.log('blobUrl:', blobUrl);

            // Create a download link
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `generated-video-${Date.now()}.mp4`;
            console.log('link:', link);

            // Trigger the download
            document.body.appendChild(link);
            console.log('document.body.appendChild(link);');
            link.click();
            console.log('link.click();');
            document.body.removeChild(link);
            console.log('document.body.removeChild(link);');

            // Clean up the blob URL
            URL.revokeObjectURL(blobUrl);
            console.log('URL.revokeObjectURL(blobUrl);');

            // Show success message and modal
            setMessage('Video downloaded successfully!');
            setMessageType('success')
            setShowMetaModal(true);
        } catch (error) {
            console.error('Error downloading video:', error);
            setMessage('Error downloading video. Please try again.');
            setMessageType('error')
        }
    };
    // Handle file upload
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setVideo(file);
        }
        console.log('file:', file);
    };

    // Handle model selection
    const handleModelChange = (event) => {
        setModel(event.target.value);
    };

    // Handle file drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files).filter(file =>
            file.type.startsWith('video/') || file.type.startsWith('image/')
        );

        if (files.length > 50) {
            setBackendError('You can only upload up to 50 files at once');
            return;
        }

        handleFiles(files);
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    // Process files
    const handleFiles = async (files) => {
        for (const file of files) {
            const formData = new FormData();
            formData.append('video', file);

            try {
                const response = await api.uploadVideo(formData, {
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(prev => ({
                            ...prev,
                            [file.name]: progress
                        }));
                    }
                });

                const newVideo = {
                    id: response.data.id,
                    name: file.name,
                    url: response.data.url,
                    status: 'processing',
                    tags: [],
                    description: '',
                    cuts: []
                };

                setVideos(prev => [...prev, newVideo]);

                // Clear progress after successful upload
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Auto-cuts are now generated automatically in the backend
                console.log('Video uploaded successfully, auto-cuts will be generated in the background');
            } catch (error) {
                console.error('Upload error:', error);
                setBackendError(`Failed to upload ${file.name}`);
            }
        }
    };

    // Handle tag addition
    const handleAddTag = async (videoId) => {
        if (!newTag.trim()) return;

        try {
            await api.addVideoTag(videoId, newTag.trim());

            // Update local state
            setVideos(prev => prev.map(video => {
                if (video.id === videoId) {
                    return {
                        ...video,
                        tags: [...video.tags, newTag.trim()]
                    };
                }
                return video;
            }));

            setNewTag('');
        } catch (error) {
            console.error('Error adding tag:', error);
            setBackendError(`Failed to add tag: ${error.response?.data?.error || error.message}`);
        }
    };

    // Handle tag removal
    const handleRemoveTag = async (videoId, tagToRemove) => {
        try {
            await api.removeVideoTag(videoId, tagToRemove);

            // Update local state
            setVideos(prev => prev.map(video => {
                if (video.id === videoId) {
                    return {
                        ...video,
                        tags: video.tags.filter(tag => tag !== tagToRemove)
                    };
                }
                return video;
            }));
        } catch (error) {
            console.error('Error removing tag:', error);
            setBackendError(`Failed to remove tag: ${error.response?.data?.error || error.message}`);
        }
    };

    // Handle description update
    const handleUpdateDescription = (videoId) => {
        setVideos(prev => prev.map(video => {
            if (video.id === videoId) {
                return {
                    ...video,
                    description: newDescription
                };
            }
            return video;
        }));
        setEditingVideo(null);
        setNewDescription('');
    };

    // Fetch generated videos
    // useEffect(() => {
    //     const fetchGeneratedVideos = async () => {
    //         try {
    //             const response = await api.getUserGeneratedVideos(userId);
    //             setGeneratedVideos(response.data);
    //         } catch (error) {
    //             console.error('Error fetching generated videos:', error);
    //         }
    //     };

    //     if (userId) {
    //         fetchGeneratedVideos();
    //     }
    // }, [userId]);

    return (
        <div
            className="gradient-outline"
            style={{
                background: gradientMode ? getCurrentGradient() : 'transparent'
            }}
        >
            <div className="workspace" style={{
                borderRadius: gradientMode ? '5px' : '0px',
                margin: gradientMode ? '12px 13px 12px 13px' : '0px',
                border: 'var(--input-bg)',
                outline: darkMode ? '6px solid #1a1a1a90' : '6px solid #ffffff90',
                boxShadow: '0 0 4px 8px  #1a1a1a30',
                // boxShadow: darkMode ? '0 0 2px 4px  #1a1a1a70' : '0 0 2px 4px #ffffff70',
                transition: 'border-radius 0.3s ease, margin 0.3s ease',
                // backdropFilter: gradientMode ? 'blur(10px)' : 'none',
                // background: gradientMode ? getCurrentGradient() : 'transparent',
                padding: screenWidth <= 600 ? '10px 10px 10px 10px' : ''
            }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                    <div>
                        <h1 style={{ marginBottom: '0px', marginTop: screenWidth <= 600 ? '0px' : '' }}>Merra.ai <span style={{ fontSize: '12px', color: 'var(--text-color)', opacity: 0.7 }}>beta</span></h1>
                        {(() => {
                            try {
                                const deployDate = process.env.REACT_APP_LAST_DEPLOY_DATE;
                                console.log('Deploy date env var:', `"${deployDate}"`, 'Type:', typeof deployDate); // Debug log
                                if (deployDate) {
                                    const date = new Date(deployDate);
                                    console.log('Parsed date:', date); // Debug log
                                    if (!isNaN(date.getTime())) {
                                        return (
                                            <div style={{
                                                fontSize: '10px',
                                                color: 'var(--text-secondary)',
                                                opacity: 0.6,
                                                marginTop: '4px'
                                            }}>
                                                Last code update: {(() => {
                                                    const now = new Date();
                                                    const diffMs = now - date;
                                                    const diffMins = Math.floor(diffMs / 60000);
                                                    const diffHours = Math.floor(diffMs / 3600000);
                                                    const diffDays = Math.floor(diffMs / 86400000);
                                                    if (diffMins < 1) return 'just now';
                                                    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
                                                    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
                                                    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
                                                    return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                                                })()}
                                            </div>
                                        );
                                    }
                                }
                            } catch (e) {
                                console.warn('Failed to parse deploy date:', e);
                            }
                            return null;
                        })()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Palette Icon - only show when gradient mode is active */}
                        {gradientMode && (
                            <div style={{ position: 'relative' }} data-gradient-palette>
                                <button
                                    onClick={() => setShowGradientPalette(!showGradientPalette)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--text-color)',
                                        opacity: 0.7,
                                        transition: 'opacity 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                                    onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                                >
                                    <svg width="20" height="20" viewBox="0 0 15 18" fill="currentColor">
                                        <path d="M8 5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3m4 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M5.5 7a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m.5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                                        <path d="M16 8c0 3.15-1.866 2.585-3.567 2.07C11.42 9.763 10.465 9.473 10 10c-.603.683-.475 1.819-.351 2.92C9.826 14.495 9.996 16 8 16a8 8 0 1 1 8-8m-8 7c.611 0 .654-.171.655-.176.078-.146.124-.464.07-1.119-.014-.168-.037-.37-.061-.591-.052-.464-.112-1.005-.118-1.462-.01-.707.083-1.61.704-2.314.369-.417.845-.578 1.272-.618.404-.038.812.026 1.16.104.343.077.702.186 1.025.284l.028.008c.346.105.658.199.953.266.653.148.904.083.991.024C14.717 9.38 15 9.161 15 8a7 7 0 1 0-7 7" />
                                    </svg>
                                </button>

                                {/* Gradient Palette Dropdown */}
                                {showGradientPalette && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        ...(window.innerWidth <= 600 ? {
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            right: 'auto'
                                        } : {
                                            right: '0'
                                        }),
                                        marginTop: '8px',
                                        background: 'var(--input-bg)',
                                        border: '1px solid var(--input-border)',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        zIndex: 99999,
                                        minWidth: '200px'
                                    }}>
                                        <div style={{
                                            fontSize: '12px',
                                            color: 'var(--text-color)',
                                            opacity: 0.7,
                                            marginBottom: '8px',
                                            fontWeight: '500'
                                        }}>
                                            Choose Gradient
                                        </div>

                                        {Object.entries(gradientPresets).map(([key, gradient]) => (
                                            <button
                                                key={key}
                                                onClick={() => {
                                                    setSelectedGradient(key);
                                                    setShowGradientPalette(false);
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 12px',
                                                    margin: '4px 0',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    background: selectedGradient === key ? 'var(--accent-color)' : 'transparent',
                                                    color: selectedGradient === key ? 'var(--text-color)' : 'var(--text-color)',
                                                    fontWeight: selectedGradient === key ? 'bold' : 'normal',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    fontSize: '14px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (selectedGradient !== key) {
                                                        e.target.style.background = 'var(--hover-bg)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (selectedGradient !== key) {
                                                        e.target.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '4px',
                                                    background: gradient,
                                                    border: '1px solid var(--input-border)',
                                                    outline: selectedGradient === key ? '2px solid var(--input-border)' : 'none',
                                                    flexShrink: 0
                                                }} />
                                                <span style={{ textTransform: 'capitalize' }}>
                                                    {key}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Gradient Switch */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                gap: '8px'
                            }}>
                                <span style={{
                                    fontSize: '14px',
                                    color: 'var(--text-color)',
                                    opacity: 0.8
                                }}>
                                    {/* Gradient */}
                                </span>
                                <div style={{
                                    position: 'relative',
                                    width: '38px',
                                    height: '18px',
                                    borderRadius: '13px',
                                    outline: '3px solid var(--input-border)',
                                    background: gradientMode
                                        ? (getCurrentGradient() === gradientPresets.classic ? 'linear-gradient(150deg, #FF1E78 0%,  #a03cff 100%)' : getCurrentGradient())
                                        : '#808080',
                                    transition: 'background 0.3s ease',
                                    cursor: 'pointer'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: '2px',
                                        left: gradientMode ? '22px' : '2px',
                                        width: '14px',
                                        height: '14px',
                                        borderRadius: '50%',
                                        outline: '3px solid var(--input-border)',
                                        backgroundColor: 'white',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }} />
                                </div>
                                <input
                                    type="checkbox"
                                    checked={gradientMode}
                                    onChange={(e) => {
                                        console.log('🔄 Toggle changed to:', e.target.checked);
                                        setGradientMode(e.target.checked);
                                    }}
                                    style={{ display: 'none' }}
                                />
                            </label>
                        </div>
                        {/* {userProfile.isSubscribed && (
                        <span style={{
                            backgroundColor: '#000000',
                            border: '1px solid #FFD700',
                            color: '#FFD700',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            letterSpacing: '0.5px'
                        }}>
                            {userProfile.subscriptionPlan === 'creator' ? 'Creator' : 'PRO'}
                        </span>
                    )} */}
                        <button
                            className="profile-button"
                            onClick={() => setShowProfileModal(true)}
                            aria-label="Settings"
                            style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                border: '1px solid var(--input-border)',
                                cursor: 'pointer',
                                position: 'relative',
                                padding: 0,
                                backgroundColor: 'var(--input-bg)',
                                color: 'var(--text-color)',
                                marginLeft: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.backgroundColor = darkMode ? '#32363b' : '#ebebeb';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '';
                                e.currentTarget.style.backgroundColor = 'var(--input-bg)';
                            }}
                        >
                            {/* {userInfo.picture ? (
                                <img
                                    src={userInfo.picture}
                                    alt="Profile"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: '50%',
                                        backgroundColor: 'var(--input-bg)'
                                    }}
                                />
                            ) : (
                                <i className="fas fa-user" style={{ color: 'var(--text-color)' }}></i>
                            )} */}
                            <Settings size={18} color="currentColor" aria-hidden="true" />

                            {/* Notification Dot */}
                            {/* {!profileComplete && (
                            <span style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                width: '12px',
                                height: '12px',
                                backgroundColor: '#ff4444',
                                borderRadius: '50%',
                                border: '2px solid white',
                            }} />
                        )} */}
                        </button>
                    </div>
                </div>


                {/* error banner */}
                {(backendError || showServerStartingMessage) && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', alignSelf: 'center', justifySelf: 'center' }}>
                        <div style={{
                            backgroundColor: showServerStartingMessage ? '#fff3cd' : '#ff44444d',
                            color: showServerStartingMessage ? '#856404' : '#ff4444',
                            padding: '12px 20px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            border: showServerStartingMessage ? '1px solid #ffeeba' : '1px solid #ff4444',
                            maxWidth: '500px',
                            alignSelf: 'center',
                            justifySelf: 'center',
                        }}>
                            {showServerStartingMessage ? (
                                <>
                                    <RefreshCw className="spin" size={18} />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
                                        <span>
                                            Server fell asleep, warming it up right now...
                                            <br />
                                            {countdown > 0 ? (
                                                `This may take ~${countdown} second${countdown !== 1 ? 's' : ''}.`
                                            ) : (
                                                'Starting...'
                                            )}
                                        </span>
                                        <div style={{
                                            width: '100%',
                                            height: '8px',
                                            backgroundColor: '#856404',
                                            borderRadius: '4px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${serverWakeProgress}%`,
                                                height: '100%',
                                                backgroundColor: '#D4A853',
                                                borderRadius: '4px',
                                                transition: 'width 2s ease-in-out'
                                            }} />
                                        </div>
                                        {/* <span style={{ fontSize: '12px', opacity: 0.8 }}>
                                        {serverWakeProgress}%
                                    </span> */}
                                        <Link
                                            to="/status"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: '12px',
                                                color: '#856404',
                                                textDecoration: 'underline',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                marginTop: '4px'
                                            }}
                                        >
                                            Check status
                                            <ExternalLink size={12} />
                                        </Link>
                                    </div>
                                    <img src="/dancing-happy-dance.gif" alt="loading" style={{ width: '90px', height: '90px', marginLeft: '10px' }} />
                                </>
                            ) : (
                                <>
                                    <AlertCircle size={20} />
                                    {backendError}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Profile Modal */}
                {showProfileModal && <ProfileModal />}

                {/* Floating setup pill — disabled; replaced by OnboardingOverlay */}
                {false && currentWorkspace?.showSetupSteps && !setupRequiredComplete && !setupStepsInView && (() => {
                    if (currentSetupStepIndex < 0) return null;
                    const step = setupSteps[currentSetupStepIndex];
                    return (
                        <button
                            type="button"
                            onClick={() => navigateToSetupTab(step.tab)}
                            style={{
                                position: 'fixed',
                                top: '24px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                padding: '10px 20px',
                                borderRadius: '999px',
                                backgroundColor: darkMode ? 'rgba(225, 246, 121, 0.12)' : 'rgba(225, 246, 121, 0.35)',
                                border: darkMode ? '1px solid rgba(225, 246, 121, 0.55)' : '1px solid #c5d96a',
                                boxShadow: darkMode ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.12)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                maxWidth: 'calc(100vw - 48px)',
                                cursor: 'pointer',
                                font: 'inherit',
                                color: 'inherit'
                            }}
                        >
                            <span style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: '#e1f679',
                                color: '#1a1a1a',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontSize: '12px',
                                fontWeight: 'bold'
                            }}>
                                {currentSetupStepIndex + 1}
                            </span>
                            <span style={{
                                fontSize: '14px',
                                fontWeight: 600,
                                color: darkMode ? '#e1f679' : '#3f4d00'
                            }}>
                                {step.label}
                            </span>
                        </button>
                    );
                })()}

                {/* Confetti overlay when workspace setup is completed */}
                {showSetupConfetti && (
                    <canvas
                        ref={setupConfettiCanvasRef}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            pointerEvents: 'none',
                            zIndex: 99999
                        }}
                        width={typeof window !== 'undefined' ? window.innerWidth : 800}
                        height={typeof window !== 'undefined' ? window.innerHeight : 600}
                    />
                )}

                {/* Greeting */}
                {/* <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                    <h2 style={{
                        fontSize: window.innerWidth <= 600 ? '20px' : '24px',
                        margin: '10px 0'
                    }}>Hello {userName?.split(' ')[0]}!</h2>
                    <h2 className={darkMode ? "gradient-text-dark" : "gradient-text"} style={{
                        justifySelf: "center",
                        fontSize: window.innerWidth <= 600 ? '20px' : '20px',
                        margin: '10px 0'
                    }}>
                        Feeling awesome today?
                    </h2>
                </div> */}

                {/* Workspace setup progress — disabled; replaced by OnboardingOverlay */}
                {false && currentWorkspace?.showSetupSteps && !setupRequiredComplete && (
                    <div
                        ref={setupStepsRef}
                        style={{
                            marginBottom: '24px',
                            padding: '16px',
                            borderRadius: '12px',
                            backgroundColor: darkMode ? 'rgba(45, 55, 72, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                            border: darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0',
                            boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.08)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: screenWidth <= 600 ? 'column' : 'row',
                            gap: screenWidth <= 600 ? '12px' : '16px',
                            alignItems: screenWidth <= 600 ? 'stretch' : 'center',
                            flexWrap: 'wrap',
                            overflow: 'visible'
                        }}>
                            {setupSteps.map((step, index) => {
                                const isCurrent = index === currentSetupStepIndex;
                                return (
                                <button
                                    type="button"
                                    key={index}
                                    onClick={() => navigateToSetupTab(step.tab)}
                                    style={{
                                        flex: screenWidth <= 600 ? 'none' : 1,
                                        minWidth: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        backgroundColor: step.done
                                            ? (darkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)')
                                            : isCurrent
                                                ? (darkMode ? 'rgba(225, 246, 121, 0.12)' : 'rgba(225, 246, 121, 0.35)')
                                                : 'transparent',
                                        border: step.done
                                            ? (darkMode ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid rgba(34, 197, 94, 0.3)')
                                            : isCurrent
                                                ? (darkMode ? '1px solid rgba(225, 246, 121, 0.55)' : '1px solid #c5d96a')
                                                : step.optional
                                                    ? (darkMode ? '1px dashed #4a5568' : '1px dashed #cbd5e1')
                                                    : (darkMode ? '1px solid #4a5568' : '1px solid #e2e8f0'),
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        font: 'inherit',
                                        color: 'inherit',
                                        width: screenWidth <= 600 ? '100%' : undefined,
                                        boxShadow: isCurrent ? (darkMode ? '0 0 0 1px rgba(225, 246, 121, 0.2)' : '0 2px 8px rgba(225, 246, 121, 0.35)') : 'none',
                                        overflow: 'visible'
                                    }}
                                >
                                    <span style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        backgroundColor: step.done
                                            ? '#22c55e'
                                            : isCurrent
                                                ? '#e1f679'
                                                : (darkMode ? '#4a5568' : '#cbd5e1'),
                                        color: step.done ? 'white' : isCurrent ? '#1a1a1a' : 'white',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        fontSize: '12px',
                                        fontWeight: 'bold'
                                    }}>
                                        {step.done ? <Check size={14} /> : index + 1}
                                    </span>
                                    <span style={{
                                        position: 'relative',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        fontSize: screenWidth <= 600 ? '14px' : '15px',
                                        color: step.done
                                            ? (darkMode ? '#86efac' : '#166534')
                                            : isCurrent
                                                ? (darkMode ? '#e1f679' : '#3f4d00')
                                                : (darkMode ? '#a0aec0' : '#64748b'),
                                        textDecoration: step.done ? 'line-through' : 'none',
                                        fontStyle: step.optional && !step.done ? 'italic' : 'normal',
                                        fontWeight: isCurrent ? 600 : 400
                                    }}>
                                        {step.label}
                                        {setupStepSparkles[index] != null && (
                                            <SetupStepSparkleBurst
                                                key={setupStepSparkles[index]}
                                                onDone={() => clearSetupStepSparkle(index)}
                                            />
                                        )}
                                    </span>
                                </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Video Management Component */}
                <WorkspaceManagement
                    disableCuts={true}
                    onboardingCompleted={onboardingCompleted}
                    referralSource={referralSource}
                    onReferralSourceSaved={(source) => setReferralSource(source)}
                    onOnboardingComplete={() => setOnboardingCompleted(true)}
                    onSetupProgress={({ step1, step2, step3, step4 }) => {
                        setSetupStep1(!!step1);
                        setSetupStep2(!!step2);
                        setSetupStep3(!!step3);
                        setSetupStep4(!!step4);
                    }}
                    onSetupStep3Complete={() => {
                        const id = currentWorkspace?._id;
                        setSetupStep5(true);
                        if (id) {
                            updateWorkspace(id, {
                                setupStepsProgress: {
                                    ...(currentWorkspace?.setupStepsProgress || {}),
                                    studioVideoCreated: true
                                }
                            }, { skipLoading: true }).catch((err) => {
                                console.error('Failed to persist studio setup step:', err);
                            });
                        }
                        setShowSetupConfetti(true);
                    }}
                />


                {/* Year Progress Bar */}
                {/* <div style={{
                    padding: '20px',
                    marginTop: '30px',
                    marginBottom: '30px',
                    borderTop: darkMode ? '1px solid #222529' : '1px solid #666',
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        // color: '#ffffff95',
                        opacity: '0.8',
                        fontSize: '14px'
                    }}>
                        <span>Year {new Date().getFullYear()} progression (reminder to do the most)</span>
                        <span>{calculateYearProgress()}% complete</span>
                    </div>
                    <div style={{
                        width: '100%',
                        height: '4px',
                        backgroundColor: darkMode ? '#2A2B2F' : '#214B23',
                        borderRadius: '2px',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{
                            width: `${calculateYearProgress()}%`,
                            height: '100%',
                            backgroundColor: '#4CAF50',
                            borderRadius: '2px',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>
                    {parseFloat(calculateYearProgress()) > 50 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '8px',
                            fontSize: '12px',
                            opacity: '1',
                        }}>
                            <span style={{ color: '#4CAF50' }}>👆<span style={{ opacity: '0.7' }}> experience</span></span>
                            <span style={{ color: darkMode ? '#888' : '#666' }}>
                                {(() => {
                                    const daysRemaining = getDaysRemainingInYear();
                                    return daysRemaining < 50 ? `(${daysRemaining} days) ` : '';
                                })()}
                                <span style={{ opacity: '0.7' }}>opportunity</span> ☝️
                            </span>
                        </div>
                    )}
                </div> */}

                {/* Feedback Button */}
                <FeedbackButton username={userName} />

                {/* Error Modal */}
                {showErrorModal && (
                    <div className="modal" onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowErrorModal(false);
                        }
                    }}>
                        <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                            <div style={{
                                fontSize: '48px',
                                marginBottom: '16px',
                                color: '#dc3545'
                            }}>
                                <AlertCircle size={48} />
                            </div>
                            <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                                Error
                            </h3>
                            <p style={{
                                marginBottom: '24px',
                                color: '#666',
                                fontSize: '16px',
                                lineHeight: '1.5'
                            }}>
                                {errorMessage}
                            </p>
                            <div className="modal-actions">
                                <button
                                    onClick={() => setShowErrorModal(false)}
                                    style={{
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Success Modal */}
                {showSuccessModal && (
                    <div className="modal" onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowSuccessModal(false);
                        }
                    }}>
                        <div className="modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                            <div style={{
                                fontSize: '48px',
                                marginBottom: '16px',
                                color: '#28a745'
                            }}>
                                <CheckCircle size={48} />
                            </div>
                            <h3 style={{ marginBottom: '16px', color: darkMode ? '#fff' : '#333' }}>
                                Success!
                            </h3>
                            <p style={{
                                marginBottom: '24px',
                                color: '#666',
                                fontSize: '16px',
                                lineHeight: '1.5'
                            }}>
                                {successMessage}
                            </p>
                            <div className="modal-actions">
                                <button
                                    onClick={() => setShowSuccessModal(false)}
                                    style={{
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default Workspace;